/* Play It Koi — Pond Volume Calculator (Shapes + Irregular Outline)
   - Predefined: Rectangle, Circle, Oval (ellipse), L-shape
   - Irregular Outline (click-to-draw polygon) with scale calibration (ft per pixel)
   - Results: Gallons, Liters, Surface area (ft²). Summary: headline volume.
   - Uses React UMD loaded by the section.
   Filename pik-volume-app.js
*/

(function () {
  var ROOTS = new WeakMap(); // el -> {root, props}
  function h(t, p) {
    var c = [].slice.call(arguments, 2);
    return React.createElement.apply(null, [t, p || {}].concat(c));
  }

  // ---------- Utilities ----------
  function num(v, f) { var n = parseFloat(v); return Number.isFinite(n) ? n : (f || 0); }
  function clamp(x, min, max) { if (!Number.isFinite(x)) return min; if (min!=null && x<min) return min; if (max!=null && x>max) return max; return x; }
  function toFixedSafe(x, n) { return Number.isFinite(x) ? x.toFixed(n) : "0"; }
  // [PIK] pretty number formatting (locale-aware, tabular-friendly)
var NF0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
var NF1 = new Intl.NumberFormat(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
var NF2 = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function fmt(n, places) {
  if (!Number.isFinite(n)) return "0";
  switch (places) {
    case 0: return NF0.format(n);
    case 1: return NF1.format(n);
    case 2: return NF2.format(n);
    default: return new Intl.NumberFormat(undefined, { maximumFractionDigits: places || 2 }).format(n);
  }
}
// --- Shape polygon helpers for clipping / coverage in presets ---
// Rectangle polygon in *SVG px*
function makeRectPtsPx(x0, y0, x1, y1){
  return [
    {x:x0, y:y0}, {x:x1, y:y0}, {x:x1, y:y1}, {x:x0, y:y1}
  ];
}

// Ellipse polygon approximation in *SVG px*
// cx,cy in px; rx,ry in px; n≥24 recommended
function makeEllipsePtsPx(cx, cy, rx, ry, n){
  n = Math.max(24, n|0);
  var out = new Array(n);
  for (var i=0; i<n; i++){
    var t = (i / n) * Math.PI * 2;
    out[i] = { x: cx + rx * Math.cos(t), y: cy + ry * Math.sin(t) };
  }
  return out;
}

  function sq(x){ return x*x; }
// --- Feet/Inches helpers ---
function feetToFtIn(n){
  n = Math.max(0, +n || 0);
  var ft = Math.floor(n);
  var inches = Math.round((n - ft) * 12);
  if (inches === 12) { ft += 1; inches = 0; }
  return { ft: ft, inches: inches };
}
function formatFtIn(n){
  var p = feetToFtIn(n);
  return p.ft + "’ " + p.inches + "”";
}
// Accepts: 12’ 3”, 12' 3", 12 3, 12ft 3in, or 12.25 (decimal feet)
function parseFtInToFeet(s){
  if (s == null) return null;
  var str = String(s).trim().toLowerCase();
  if (!str) return null;


  // if pure number → decimal feet
  if (/^[+-]?\d+(\.\d+)?$/.test(str)) return parseFloat(str);

  // normalize quotes/words
  str = str.replace(/[’′]/g, "'").replace(/[”″]/g, '"')
           .replace(/\bft\b|\bfeet\b/g, "'").replace(/\bin\b|\binches\b/g, '"');

  var ftMatch = /([+-]?\d+(\.\d+)?)\s*'/.exec(str);
  var inMatch = /([+-]?\d+(\.\d+)?)\s*"/.exec(str);

  if (ftMatch || inMatch){
    var ft = ftMatch ? parseFloat(ftMatch[1]) : 0;
    var inches = inMatch ? parseFloat(inMatch[1]) : 0;
    if (!Number.isFinite(ft) || ft < 0) ft = 0;
    if (!Number.isFinite(inches) || inches < 0) inches = 0;
    return ft + inches/12;
  }

  // fallback: "12 3" (feet inches)
  var parts = str.split(/\s+/).map(parseFloat).filter(Number.isFinite);
  if (parts.length === 2) return Math.max(0, parts[0]) + Math.max(0, parts[1])/12;

  return null;
}
// Interprets free-form user input. Rules:
// - If it has " or 'in'  => treat inches (or ft+in via parseFtInToFeet)
// - If it has ' or 'ft'  => treat feet (or ft+in via parseFtInToFeet)
// - If it's a plain number:
//      * with a decimal => decimal feet (8.3333 = 8.3333 ft)
//      * integer <= 24  => feet (10 = 10 ft)
//      * integer  > 24  => inches (96 = 8 ft)
function parseFeetSmart(s){
  if (s == null) return null;
  var str = String(s).trim();
  if (!str) return null;

  var norm = str.toLowerCase()
    .replace(/[’′]/g, "'")
    .replace(/[”″]/g, '"')
    .replace(/\bfeet\b|\bft\b/g, "'")
    .replace(/\binches\b|\bin\b/g, '"');

  // explicit units → let the robust parser handle it
  if (norm.includes('"') || norm.includes("'")) return parseFtInToFeet(norm);

  // pure number?
  if (/^[+-]?\d+(\.\d+)?$/.test(norm)) {
    if (norm.includes('.')) return parseFloat(norm); // decimal feet
    var n = parseInt(norm, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    return (n > 24) ? (n / 12) : n;
  }

  // fallback to the robust parser
  return parseFtInToFeet(norm);
}

// ---------- Interaction Adapter (interact.js or graceful no-op) ----------
var InteractAdapter = (function(){
  var has = !!(window.interact);

  function attach(el, id, cbs, opts){
    if (!el || !cbs) return function(){};
    if (!has) return function(){};

    // We do snapping in value space inside onMove, so no interact modifiers.
    var interactable = window.interact(el).draggable({
      listeners: {
        start: function(){ cbs.onStart && cbs.onStart({ id:id }); },
        move:  function(e){ cbs.onMove  && cbs.onMove({ id:id, dx:e.dx, dy:e.dy }); },
        end:   function(){ cbs.onEnd   && cbs.onEnd({ id:id }); }
      },
      inertia: false
    });

    // Detach helper for cleanup
    return function(){ try { interactable && interactable.unset && interactable.unset(); } catch(e){} };
  }

  return { attach: attach };
})();

// ---- Feet/inches helpers ----
function snapToInches(ft, stepInches){           // stepInches: 1 → 1", 3 → 3", etc.
  var s = Math.max(1, stepInches|0);
  var totalIn = Math.round((ft * 12) / s) * s;   // snap in integer inches
  return totalIn / 12;
}
function ftToInput(ft){                           // clean string for <input>
  var v = Math.round(ft * 12) / 12;               // keep to nearest 1"
  return v.toFixed(3).replace(/\.?0+$/,'');       // trim trailing zeros
}
function fmtFeetInches(ft){                       // nice label like 20′ 2″
  if (!Number.isFinite(ft)) return "0′ 0″";
  var totalIn = Math.round(ft * 12);
  var feet = Math.floor(totalIn / 12);
  var inches = totalIn % 12;
  return feet + "′ " + inches + "″";
}


  // Areas (ft² for param in feet)
  function areaRectangle(L, W) { return Math.max(0, L) * Math.max(0, W); }
  function areaCircle(D) { var r = Math.max(0, D)/2; return Math.PI*r*r; }
  function areaEllipse(A, B) { var a=Math.max(0,A)/2, b=Math.max(0,B)/2; return Math.PI*a*b; }
  function areaLShape(Lo, Wo, Li, Wi) {
    Lo=Math.max(0,Lo); Wo=Math.max(0,Wo); Li=Math.max(0,Li); Wi=Math.max(0,Wi);
    if (Li>=Lo || Wi>=Wo) return Lo*Wo;
    return Lo*Wo - Li*Wi;
  }

  // Irregular outline helpers (SVG px → ft via ftPerPx)
  function polygonAreaPx(pts) {
    var n=pts.length; if (n<3) return 0;
    var s=0;
    for (var i=0;i<n;i++){ var j=(i+1)%n; s += pts[i].x*pts[j].y - pts[j].x*pts[i].y; }
    return Math.abs(s/2);
  }
  // [PIK] point-in-polygon (ray cast) — global (used by painter + compute)
function pointInPolygon(pt, poly) {
  var x = pt.x, y = pt.y, inside = false;
  for (var i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    var xi = poly[i].x, yi = poly[i].y;
    var xj = poly[j].x, yj = poly[j].y;
    var intersect = ((yi > y) !== (yj > y)) &&
      (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

  function dist(p1,p2){ var dx=p1.x-p2.x, dy=p1.y-p2.y; return Math.sqrt(dx*dx+dy*dy); }

// [PIK] depth color (one canonical version)
function depthColor(d, max){
  var v = Math.max(0, +d || 0);
  var t = max > 0 ? Math.min(1, v / max) : 0;
  var hue = 210 - t * 15;      // blue range with more variation (210 -> 195)
  var sat = 85 - t * 15;       // saturation varies 85% -> 70%
  var light = 88 - t * 68;     // MUCH wider range: 88% (very light) -> 20% (deep navy)
  return "hsl(" + hue + " " + sat + "% " + light + "%)";
}

// [PIK] derive square cell grid from desired cell size (ft)
function gridDimsFromCell(ftPerPx, cellFt, VBW, VBH) {
  var px = (ftPerPx > 0 && cellFt > 0) ? (cellFt / ftPerPx) : 40; // fallback px
  var nx = Math.max(8, Math.ceil(VBW / px));
  var ny = Math.max(8, Math.ceil(VBH / px));
  return { nx: nx, ny: ny, cellPx: px }; // cell is square: px × px
}

// [PIK] coverage for a SQUARE cell at index (ix,iy) with side cellPx
function cellCoverageFractionSquare(ix, iy, cellPx, VBW, VBH, poly, S) {
  S = Math.max(1, S|0);
  var x0 = ix * cellPx, y0 = iy * cellPx;
  var inside = 0, total = S*S;
  for (var sy=0; sy<S; sy++){
    for (var sx=0; sx<S; sx++){
      var px = x0 + (sx + 0.5) * (cellPx / S);
      var py = y0 + (sy + 0.5) * (cellPx / S);
      if (px < 0 || py < 0 || px > VBW || py > VBH) continue; // outside view
      if (pointInPolygon({x:px, y:py}, poly)) inside++;
    }
  }
  return inside / total;
}


// ---------- Error Boundary (shows render errors in-place) ----------
function ErrorBox(props){
  return h("div", {
    style:{
      border:"1px solid #ef4444", background:"rgba(239,68,68,.06)",
      color:"#991b1b", padding:"8px", borderRadius:"8px", margin:"8px 0"
    }
  },
    h("div", {style:{fontWeight:700, marginBottom:4}}, "Something broke in this section"),
    props.message ? h("div", {style:{whiteSpace:"pre-wrap", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", fontSize:"12px"}}, String(props.message)) : null,
    props.stack ? h("div", {style:{whiteSpace:"pre-wrap", fontFamily:"ui-monospace, SFMono-Regular, Menlo, monospace", fontSize:"11px", marginTop:6}}, String(props.stack)) : null
  );
}

class ErrorBoundary extends React.Component {
  constructor(p){ super(p); this.state = { hasError:false, error:null, info:null }; }
  static getDerivedStateFromError(error){ return { hasError:true, error:error }; }
  componentDidCatch(error, info){ this.setState({ info }); console.error("[PIK_VOLUME] Render error:", error, info); }
  render(){
    if (this.state.hasError) {
      var msg = (this.state.error && this.state.error.message) || this.state.error || "Unknown error";
      var stack = (this.state.error && this.state.error.stack) || (this.state.info && this.state.info.componentStack) || "";
      return h(ErrorBox, { message: msg, stack: stack });
    }
    return this.props.children;
  }
}

// ---------- Reusable field ----------
function FieldNumber(props) {
  var id = props.id, label = props.label, hint = props.hint;
  var rawValue = props.value;
  var setValue = (typeof props.onChange === "function")
    ? props.onChange
    : function () {}; // no-op safety



  // Always pass a string to <input value=...>
  var safeValue = (rawValue === null || rawValue === undefined) ? "" : String(rawValue);

  var step = props.step || "0.1";
  var min = props.min || "0";
  var inputMode = props.inputMode || "decimal";

  return h("label", { htmlFor: id },
    h("span", { className: "pik-label-title" }, label),
    h("input", {
      id: id, className: "pik-input", type: "number",
      min: String(min), step: String(step), value: safeValue,
      onChange: function (e) { setValue(e.target.value); },
      inputMode: inputMode
    }),
    hint ? h("div", { className: "pik-hint" }, hint) : null
  );
}

// ---------- Feet/Inches input (text) ----------
// Accepts: 100", 8'4", 8 ft 4 in, 8.3333 (decimal ft), 12 3, etc.
// No auto-reformat while focused; commit/normalize on blur.
function FieldFeetInches(props){
  var id = props.id, label = props.label, hint = props.hint;
  var rawFeet = Math.max(0, parseFloat(props.value) || 0);
  var setValue = (typeof props.onChange === "function") ? props.onChange : function(){};
  var hideHint = props.hideHint || false;  // NEW: optional prop to hide hint

  var _t = React.useState(formatFtIn(rawFeet)), text = _t[0], setText = _t[1];
  var _f = React.useState(false), isFocused = _f[0], setFocused = _f[1];

  React.useEffect(function(){
    if (!isFocused) setText(formatFtIn(Math.max(0, parseFloat(props.value)||0)));
  }, [props.value, isFocused]);

  function onFocus(){ setFocused(true); }
  function onChange(e){ setText(e.target.value); }

  function commit(){
    var feet = parseFeetSmart(text);
    feet = Number.isFinite(feet) ? Math.max(0, feet) : 0;
    setValue(String(feet));
    setText(formatFtIn(feet));
  }
  function onBlur(){ setFocused(false); commit(); }
  function onKeyDown(e){ if (e.key === "Enter") { e.currentTarget.blur(); } }

  return h("label", { htmlFor:id },
    h("span", { className:"pik-label-title" }, label),
    h("input", {
      id:id, className:"pik-input", type:"text",
      value:text,
      onFocus:onFocus, onChange:onChange, onBlur:onBlur, onKeyDown:onKeyDown,
      inputMode:"numeric",
      placeholder:'e.g. 100", 8\'4", or 8.3333'
    }),
    // MODIFIED: Only show hint if hideHint is false
    !hideHint ? h("div", { className:"pik-hint" }, hint || 'Tip: Type 100", 8\'4", or 8.3333 (decimal feet).') : null
  );
}

// ============================================================================
// POND DATA MANAGER UI COMPONENTS
// ============================================================================

function SaveProjectButton(props) {
  var isLoggedIn = PondDataManager.isLoggedIn();
  var _showLoginModal = React.useState(false);
  var _showNameModal = React.useState(false);
  
function handleSaveClick() {
  if (!isLoggedIn) {
    _showLoginModal[1](true);
  } else {
    var activeProject = PondDataManager.getActiveProject();
    if (!activeProject) {
      // First save - show name modal
      _showNameModal[1](true);
    } else {
      // Quick save to existing project - just call onSave with existing name
      props.onSave(activeProject.project.name);
    }
  }
}
  
var activeProject = PondDataManager.getActiveProject();

return h('div', { className: 'pond-save-button-container' },
  h('button', {
    className: 'pik-btn primary',
    onClick: handleSaveClick,
    title: !isLoggedIn ? 'Log in to save your project' : 
           activeProject ? 'Save changes to ' + activeProject.project.name : 
           'Save project',
    style: { whiteSpace: 'nowrap' }
  },
    h('span', { className: 'pik-btn-icon' }, '💾'),
    ' Save'
  ),
    
    _showLoginModal[0] && h(LoginPromptModal, {
      onClose: function() { _showLoginModal[1](false); },
      onLoginSuccess: function() {
        _showLoginModal[1](false);
        _showNameModal[1](true);
      },
      onSaveDraft: props.onSaveDraft
    }),
    
    _showNameModal[0] && h(ProjectNameModal, {
      onClose: function() { _showNameModal[1](false); },
      onSave: function(name) {
        props.onSave(name);
        _showNameModal[1](false);
      }
    })
  );
}
function SaveAsButton(props) {
  var isLoggedIn = PondDataManager.isLoggedIn();
  var _showLoginModal = React.useState(false);
  var _showNameModal = React.useState(false);
  
  function handleSaveAsClick() {
    if (!isLoggedIn) {
      _showLoginModal[1](true);
    } else {
      _showNameModal[1](true);
    }
  }
  
  return h('div', { className: 'pond-save-button-container' },
    h('button', {
      className: 'pik-btn secondary',
      onClick: handleSaveAsClick,
      title: isLoggedIn ? 'Save as new project' : 'Log in to save projects',
      style: { whiteSpace: 'nowrap' }
    },
      h('span', { className: 'pik-btn-icon' }, '📋'),
      ' Save As...'
    ),
    
    _showLoginModal[0] && h(LoginPromptModal, {
      onClose: function() { _showLoginModal[1](false); },
      onLoginSuccess: function() {
        _showLoginModal[1](false);
        _showNameModal[1](true);
      },
      onSaveDraft: props.onSaveDraft
    }),
    
    _showNameModal[0] && h(ProjectNameModal, {
      onClose: function() { _showNameModal[1](false); },
      onSave: function(name) {
        props.onSave(name);
        _showNameModal[1](false);
      }
    })
  );
}
function LoginPromptModal(props) {
  function handleLoginClick() {
    // Save draft before navigating away
    if (props.onSaveDraft && typeof props.onSaveDraft === 'function') {
      props.onSaveDraft();
    }
  }
  
  return h('div', { className: 'pond-modal-overlay', onClick: props.onClose },
    h('div', {
      className: 'pond-modal',
      onClick: function(e) { e.stopPropagation(); }
    },
      h('div', { className: 'pond-modal__header' },
        h('h3', null, 'Save Your Pond Design'),
        h('button', {
          className: 'pond-modal__close',
          onClick: props.onClose,
          'aria-label': 'Close'
        }, '×')
      ),
      
      h('div', { className: 'pond-modal__body' },
        h('p', null, 'Create a Play It Koi account to save your pond design and access it from any device.'),
        h('ul', { className: 'pond-modal__benefits' },
          h('li', null, '✓ Save multiple pond projects'),
          h('li', null, '✓ Continue where you left off'),
          h('li', null, '✓ Use your design in other planning tools'),
          h('li', null, '✓ Access from any device')
        ),
        h('p', { className: 'pond-modal__note' },
          h('strong', null, 'Note: '),
          'Your current work will be preserved while you log in.'
        )
      ),
      
      h('div', { className: 'pond-modal__footer' },
        h('a', {
          href: '/account/register',
          className: 'pik-btn primary lg',
          onClick: handleLoginClick
        }, 'Create Account'),
        h('a', {
          href: '/account/login?return_to=' + encodeURIComponent(window.location.pathname),
          className: 'pik-btn secondary lg',
          onClick: handleLoginClick
        }, 'Log In')
      )
    )
  );
}

function ProjectNameModal(props) {
  var _projectName = React.useState('My Pond Project');
  
  function handleSave() {
    if (_projectName[0].trim()) {
      props.onSave(_projectName[0].trim());
    }
  }
  
  function handleKeyPress(e) {
    if (e.key === 'Enter') {
      handleSave();
    }
  }
  
  return h('div', { className: 'pond-modal-overlay', onClick: props.onClose },
    h('div', {
      className: 'pond-modal pond-modal--small',
      onClick: function(e) { e.stopPropagation(); }
    },
      h('div', { className: 'pond-modal__header' },
        h('h3', null, 'Name Your Project'),
        h('button', {
          className: 'pond-modal__close',
          onClick: props.onClose,
          'aria-label': 'Close'
        }, '×')
      ),
      
      h('div', { className: 'pond-modal__body' },
        h('label', { className: 'pond-form-label' },
          'Project Name',
          h('input', {
            type: 'text',
            className: 'pond-form-input',
            value: _projectName[0],
            onChange: function(e) { _projectName[1](e.target.value); },
            onKeyPress: handleKeyPress,
            placeholder: 'e.g., Backyard Koi Pond',
            autoFocus: true,
            maxLength: 50
          })
        )
      ),
      
      h('div', { className: 'pond-modal__footer' },
        h('button', {
          className: 'pik-btn subtle',
          onClick: props.onClose
        }, 'Cancel'),
        h('button', {
          className: 'pik-btn primary lg',
          onClick: handleSave,
          disabled: !_projectName[0].trim()
        }, 'Save Project')
      )
    )
  );
}

function ProjectStatusIndicator(props) {
  var activeProject = props.activeProject;
  var lastSaved = props.lastSaved;
  
  if (!activeProject) return null;
  
  var timeSince = lastSaved 
    ? getTimeSince(new Date(lastSaved))
    : null;
  
  return h('div', { className: 'pond-project-status' },
    h('div', { className: 'pond-project-status__name' },
      h('strong', null, 'Project: '),
      activeProject.project.name
    ),
    timeSince && h('div', { className: 'pond-project-status__saved' },
      h('span', { className: 'pond-status-icon' }, '✓'),
      ' Saved ', timeSince
    )
  );
}
// ============================================================================
// PROJECT SELECTOR/LOADER COMPONENT
// ============================================================================

function ProjectSelectorDropdown(props) {
  var _showModal = React.useState(false);
  var showModal = _showModal[0];
  var setShowModal = _showModal[1];
  
  var allProjects = PondDataManager.getAllProjects();
  var projectIds = Object.keys(allProjects);
  
  if (projectIds.length === 0) return null;
  
  function handleSelect(projectId) {
    var project = allProjects[projectId];
    if (!project) return;
    
    if (props.onLoadProject) {
      props.onLoadProject(project);
    }
    setShowModal(false);
  }
  
  return h(React.Fragment, null,
h('button', {
  className: 'pik-btn secondary',
  onClick: function() { setShowModal(true); },
  title: 'Load a saved project'
}, 
  h('span', { className: 'pik-btn-icon' }, '📂'),
  ' Load'
),
    
    showModal && h('div', { 
      className: 'pond-modal-overlay', 
      onClick: function() { setShowModal(false); }
    },
      h('div', {
        className: 'pond-modal pond-modal--small',
        onClick: function(e) { e.stopPropagation(); }
      },
        h('div', { className: 'pond-modal__header' },
          h('h3', null, 'Load Saved Project'),
          h('button', {
            className: 'pond-modal__close',
            onClick: function() { setShowModal(false); },
            'aria-label': 'Close'
          }, '×')
        ),
        
h('div', { className: 'pond-modal__body' },
  projectIds.length === 0 
    ? h('div', { className: 'pik-subtle', style: { textAlign: 'center', padding: '20px' } },
        'No saved projects yet. Create your first project by clicking Save above.')
    : h('div', { style: { maxHeight: '400px', overflowY: 'auto' } },
        projectIds.map(function(id) {
          var proj = allProjects[id];
          var name = proj.project.name || 'Untitled';
          var date = new Date(proj.project.modified).toLocaleDateString();
          var shape = proj.geometry.shape_type || 'unknown';
          
          return h('div', {
            key: id,
            className: 'pond-project-item-wrapper'
          },
            h('button', {
              className: 'pond-project-item',
              onClick: function() { handleSelect(id); }
            },
              h('div', { className: 'pond-project-item__content' },
                h('div', { className: 'pond-project-item__name' }, name),
                h('div', { className: 'pond-project-item__meta' },
                  h('span', null, shape),
                  h('span', null, ' • '),
                  h('span', null, date)
                )
              )
            ),
            h('button', {
              className: 'pond-project-item__delete',
              onClick: function(e) {
                e.stopPropagation();
                var activeId = PondDataManager.getActiveProject()?.project?.id;
                var isActive = (id === activeId);
                var confirmMsg = 'Delete "' + name + '"?' + 
                  (isActive ? ' This is your current project.' : '') + 
                  ' This cannot be undone.';
                
                if (confirm(confirmMsg)) {
                  PondDataManager.deleteProject(id);
                  
                  // Clear active project if we just deleted it
                  if (isActive) {
                    localStorage.removeItem('pond_active_project_id');
                    // Trigger re-render by closing and reopening modal
                    setShowModal(false);
                    setTimeout(function() {
                      alert('Project deleted. The calculator has been reset.');
                    }, 100);
                  } else {
                    // Just refresh the modal
                    setShowModal(false);
                    setTimeout(function() { setShowModal(true); }, 50);
                  }
                }
              },
              title: 'Delete project',
              'aria-label': 'Delete ' + name
            }, '🗑️')
          );
        })
      )
)
      )
    )
  );
}
function getTimeSince(date) {
  var seconds = Math.floor((new Date() - date) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return seconds + ' seconds ago';
  var minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + ' minute' + (minutes !== 1 ? 's' : '') + ' ago';
  var hours = Math.floor(minutes / 60);
  return hours + ' hour' + (hours !== 1 ? 's' : '') + ' ago';
}

// ---------- Irregular Outline UI ----------
function IrregularCanvas(props) {
  var points = props.points, setPoints = props.setPoints;
  var closed = props.closed, setClosed = props.setClosed;
  var drawing = props.drawing, setDrawing = props.setDrawing;
  var ftPerPx = props.ftPerPx, setFtPerPx = props.setFtPerPx;
  var refLen = props.refLen, setRefLen = props.setRefLen;
  var calibMode = props.calibMode, setCalibMode = props.setCalibMode;
  var calibFirst = props.calibFirst, setCalibFirst = props.setCalibFirst;

  var VBW = 560, VBH = 360;
  var GRID_PX = 40;
  var svgRef = React.useRef(null);

  var _isDrawing = React.useState(false), isDrawing = _isDrawing[0], setIsDrawing = _isDrawing[1];
  var _lastPt = React.useState(null), lastPt = _lastPt[0], setLastPt = _lastPt[1];
  var _calibHover = React.useState(null), calibHover = _calibHover[0], setCalibHover = _calibHover[1];

  var _promptOpen = React.useState(false), promptOpen = _promptOpen[0], setPromptOpen = _promptOpen[1];
  var _promptPx = React.useState(0), promptPx = _promptPx[0], setPromptPx = _promptPx[1];
  var _promptFt = React.useState(refLen || ""), promptFt = _promptFt[0], setPromptFt = _promptFt[1];

  var _calibA = React.useState(null), calibA = _calibA[0], setCalibA = _calibA[1];
  var _calibB = React.useState(null), calibB = _calibB[0], setCalibB = _calibB[1];

  var _eraseMode = React.useState(false), eraseMode = _eraseMode[0], setEraseMode = _eraseMode[1];
  var _isErasing = React.useState(false), isErasing = _isErasing[0], setIsErasing = _isErasing[1];
  var _calibDragging = React.useState(false), calibDragging = _calibDragging[0], setCalibDragging = _calibDragging[1];
var _calibStartPoint = React.useState(null), calibStartPoint = _calibStartPoint[0], setCalibStartPoint = _calibStartPoint[1];
var _showNameModal = React.useState(false), showNameModal = _showNameModal[0], setShowNameModal = _showNameModal[1];
var _saveMessage = React.useState(null), saveMessage = _saveMessage[0], setSaveMessage = _saveMessage[1];
  var ERASE_THRESH = 10;

  React.useEffect(function(){
    if (props.varDepth) { setEraseMode(false); setIsErasing(false); }
  }, [props.varDepth]);
// Keyboard shortcut for undo (Ctrl+Z / Cmd+Z)
React.useEffect(function(){
  function handleKeyDown(e){
    // Check for Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      // Only undo if we're in drawing mode and not in calibration or other special modes
      if (!props.varDepth && !calibMode && !promptOpen) {
        e.preventDefault();
        undo();
      }
    }
  }
  
  window.addEventListener('keydown', handleKeyDown);
  return function(){ window.removeEventListener('keydown', handleKeyDown); };
}, [points, calibMode, promptOpen, props.varDepth]); // dependencies
  var varDepthWasOnRef = React.useRef(false);

  function clientToSVG(evt) {
    var svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    var pt = svg.createSVGPoint();
    pt.x = evt.clientX; pt.y = evt.clientY;
    var m = svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    var sp = pt.matrixTransform(m.inverse());
    return { x: Math.max(0, Math.min(VBW, sp.x)), y: Math.max(0, Math.min(VBH, sp.y)) };
  }
  function dpx(p1, p2) { var dx = p1.x - p2.x, dy = p1.y - p2.y; return Math.sqrt(dx*dx + dy*dy); }

function handleCalibStart(evt) {
  var p = clientToSVG(evt);
  
  // Shift+click: discrete point placement
  if (evt.shiftKey) {
    if (!calibA) {
      // First shift-click: set point A
      setCalibA(p);
      setCalibFirst(p);
      setCalibB(null);
      setCalibHover(null);
      setPromptOpen(false);
    } else {
      // Second shift-click: set point B and show prompt
      setCalibB(p);
      var px = dpx(calibA, p);
      if (px > 0) {
        setPromptPx(px);
        setPromptFt(String(refLen || ""));
        setPromptOpen(true);
      }
      setCalibFirst(null);
      setCalibHover(null);
    }
    return;
  }
  
  // Normal click: start drag
  setCalibA(p);
  setCalibB(null);
  setCalibFirst(p);
  setCalibStartPoint(p);
  setCalibDragging(true);
  setCalibHover(p);
  setPromptOpen(false);
}

function handleCalibMove(evt) {
  if (!calibDragging) return;
  var p = clientToSVG(evt);
  setCalibHover(p);
}

function handleCalibEnd(evt) {
  if (!calibDragging) return;
  setCalibDragging(false);
  
  var p = clientToSVG(evt);
  var px = calibStartPoint ? dpx(calibStartPoint, p) : 0;
  
  if (px > 10) {
    setCalibB(p);
    setPromptPx(px);
    setPromptFt(String(refLen || ""));
    setPromptOpen(true);
  }
  
  setCalibFirst(null);
  setCalibHover(null);
}

  function eraseAtPoint(p) {
    if (!points.length) return;
    var bestIdx = -1, bestD = Infinity;
    for (var i=0;i<points.length;i++){
      var di = dpx(points[i], p);
      if (di < bestD) { bestD = di; bestIdx = i; }
    }
    if (bestD <= ERASE_THRESH && bestIdx >= 0) {
      var next = points.slice(0, bestIdx).concat(points.slice(bestIdx+1));
      setPoints(next);
      if (next.length < 3) setClosed(false);
    }
  }
  function eraseSweep(p) {
    if (!points.length) return;
    var any = false;
    var next = [];
    for (var i=0;i<points.length;i++){
      if (dpx(points[i], p) <= ERASE_THRESH) { any = true; continue; }
      next.push(points[i]);
    }
    if (any) {
      setPoints(next);
      if (next.length < 3) setClosed(false);
    }
  }

  function onPointerDown(e) {
    if (promptOpen) return;
    if (calibMode) {
  handleCalibStart(e);
  return;
}
    if (props.varDepth) return;
    var p = clientToSVG(e);

    if (eraseMode) {
      e.preventDefault();
      setIsErasing(true);
      eraseAtPoint(p);
      return;
    }

    if (closed) return;

    e.preventDefault();
    setIsDrawing(true);
    setLastPt(p);
    setPoints(points.concat([p]));
  }

  function onPointerMove(e) {
    if (props.varDepth) return;
    if (promptOpen) return;

    var p = clientToSVG(e);

if (calibMode) {
  handleCalibMove(e);
  return;
}

    if (eraseMode && isErasing) {
      eraseSweep(p);
      return;
    }

    if (!isDrawing || closed) return;

    if (!lastPt || dpx(lastPt, p) >= 4) {
      setPoints(function (arr) { return arr.concat([p]); });
      setLastPt(p);
    }
  }

  function onPointerUp(e) {
    if (props.varDepth) return;
    if (promptOpen) return;
      if (calibMode) {
    handleCalibEnd(e);
    return;
  }
    if (eraseMode && isErasing) { setIsErasing(false); return; }
    if (!isDrawing) return;
    setIsDrawing(false);
    setLastPt(null);
    if (points.length >= 3 && e && typeof e.clientX === "number") {
      var p = clientToSVG(e);
      if (dpx(points[0], p) < 12) setClosed(true);
    }
  }

  function undo() {
    if (points.length === 0) return;
    var next = points.slice(0, -1);
    setPoints(next);
    if (next.length < 3) setClosed(false);
  }
  function clearAll() {
    setPoints([]); setClosed(false);
    setCalibFirst(null); setCalibHover(null); setCalibA(null); setCalibB(null);
    if (props.setVarDepth) props.setVarDepth(false);
    setDrawing(true);
    
    setFtPerPx(0.1);
    setRefLen("10");
  }
  function closePoly() { if (points.length >= 3) setClosed(true); }

  function applyPrompt() {
    var ft = parseFloat(promptFt);
    if (Number.isFinite(ft) && ft > 0 && promptPx > 0) {
      setRefLen(String(ft));
      setFtPerPx(ft / promptPx);
      setPromptOpen(false);
      setCalibMode(false);
      setCalibA(null); setCalibB(null);
      if (props.setVarDepth && varDepthWasOnRef.current) {
        props.setVarDepth(true);
        varDepthWasOnRef.current = false;
      }
    }
  }
  function cancelPrompt() {
    setPromptOpen(false);
    setCalibA(null); setCalibB(null);
  }

  var ptsAttr = points.map(function (p) { return p.x + "," + p.y; }).join(" ");

  return React.createElement("div", null,

// Help (ABOVE everything) - editable from theme editor
(function(){
  var helpHTML = props.config && props.config.help && props.config.help.irregular
    ? props.config.help.irregular
    : "<p><strong>How to draw your pond</strong></p><ul><li><strong>1) Trace around your pond:</strong> Click along the edge to drop points, or click and drag to draw freehand. When the shape fills in with color, you've closed it! You can also click the green starting point or use the \"Close shape\" button.</li><li><strong>2) Set your scale:</strong> Click \"Calibrate\", then click two points that you know the distance between. For example, if your pond is 20 feet wide, click the left edge, then the right edge, and enter \"20\" in the box that appears above.</li><li><strong>3) Add depth:</strong> Enter your average depth in the field below, or turn on \"Depth painting\" to paint different depths for shallow shelves and deep areas.</li></ul>";
  
  return React.createElement("div", { 
    className: "pik-help", 
    style: { marginBottom: 8 },
    dangerouslySetInnerHTML: { __html: helpHTML }
  });
})(),

// Calibration + Depth + Save/Load controls (ABOVE SVG)
React.createElement("div", { className: "pik-row", style: { marginBottom: 8 } },
  React.createElement("div", {
    className: "pik-toolbar",
    style: { display: "flex", alignItems: "center", gap: 8, width: "100%", flexWrap: "wrap" }
  },
    React.createElement("button", {
      type: "button",
      className: calibMode ? "pik-btn danger" : "pik-btn primary",
      onClick: function () {
        var next = !calibMode;
        if (next) {
          if (props.varDepth && props.setVarDepth) {
            varDepthWasOnRef.current = true;
            props.setVarDepth(false);
          } else {
            varDepthWasOnRef.current = false;
          }
        } else {
          if (props.setVarDepth && varDepthWasOnRef.current) {
            props.setVarDepth(true);
            varDepthWasOnRef.current = false;
          }
        }
        setCalibMode(next);
        setCalibFirst(null); setCalibHover(null);
        if (!next) { setPromptOpen(false); setCalibA(null); setCalibB(null); }
      }
    }, calibMode ? "Cancel calibration" : "Calibrate (set scale)"),
    
    // Depth painting button
    React.createElement("button", {
      type: "button",
      className: "pik-btn",
      "aria-pressed": props.varDepth ? "true" : "false",
      style: props.varDepth
        ? { background: "rgb(56,189,248)", color: "#fff", border: "1px solid rgb(56,189,248)" }
        : { background: "#FFE219", color: "rgb(31,41,55)", border: "1px solid #FFE219" },
      onClick: function(){ if (props.setVarDepth) props.setVarDepth(!props.varDepth); }
    }, props.varDepth ? "Depth painting: ON" : "Depth painting: OFF"),
    
    // Spacer to push Save/Load to the right
    React.createElement("div", { style: { flex: 1 } }),
    

  )
),

    // Calibration prompt (ABOVE SVG, with shimmer)
    promptOpen
      ? React.createElement("div", { className: "pik-card pik-shimmer", style: { marginBottom: 8, borderColor: "#FFE219" } },
          React.createElement("div", { className: "pik-title", style: { marginBottom: 6 } }, "Set scale from known distance"),
          React.createElement("div", { className: "pik-subtle", style: { marginBottom: 8 } },
            "Enter the distance (in feet) between the two points you clicked on the canvas."
          ),
          React.createElement("div", { className: "pik-row pik-row--pair" },
            React.createElement("label", null,
              React.createElement("span", { className: "pik-label-title" }, "Known distance (ft)"),
              React.createElement("input", {
                className: "pik-input", type: "number", min: "0", step: "0.1",
                value: promptFt, onChange: function (e) { setPromptFt(e.target.value); }
              }),
              React.createElement("div", { className: "pik-hint" }, "You can adjust this value anytime later.")
            ),
            React.createElement("div", { className: "pik-toolbar", style: { marginTop: 24 } },
              React.createElement("button", { type: "button", className: "pik-btn primary", onClick: applyPrompt }, "Set scale"),
              React.createElement("button", { type: "button", className: "pik-btn subtle", onClick: cancelPrompt }, "Cancel")
            )
          )
        )
      : null,
    // Depth grid controls (when painting is ON, ABOVE SVG)
    props.varDepth
      ? React.createElement("div", { className: "pik-card pik-shimmer", style: { marginBottom: 8, borderColor: "#FFE219" } },
          React.createElement(DepthGridControls, {
            dep: props.baseDepth,
            gridBrush: props.gridBrush, setGridBrush: props.setGridBrush,
            gridCellFt: props.gridCellFt, setGridCellFt: props.setGridCellFt,
            setGridDepths: props.setGridDepths,
            ftPerPx: ftPerPx,
            gridDepths: props.gridDepths
          })
        )
      : null,

    // Canvas
    React.createElement("div", {
      style: {
        position: "relative",
        border: "1px solid var(--pik-border,#e5e7eb)",
        borderRadius: "10px",
        overflow: "hidden",
        marginTop: 8
      }
    },
      React.createElement("svg", {
        ref: svgRef,
        width: "100%",
        viewBox: "0 0 " + VBW + " " + VBH,
        style: {
          display: "block",
          background: "rgba(0,0,0,0.02)",
          touchAction: "none",
          cursor: (calibMode || (drawing && !closed)) ? "crosshair" : (eraseMode ? "not-allowed" : "default"),
          userSelect: "none"
        },
        onPointerDown: onPointerDown,
        onPointerMove: onPointerMove,
        onPointerUp: onPointerUp,
        onPointerLeave: function () {
          if (isDrawing) { setIsDrawing(false); setLastPt(null); }
          if (isErasing) setIsErasing(false);
        },
      },
        React.createElement("defs", null,
          React.createElement("pattern", { id: "irr-grid", width: GRID_PX, height: GRID_PX, patternUnits: "userSpaceOnUse" },
            React.createElement("path", { d: "M " + GRID_PX + " 0 L 0 0 0 " + GRID_PX, fill: "none", stroke: "rgba(0,0,0,0.06)", strokeWidth: 1 })
          )
        ),
        React.createElement("rect", { width: VBW, height: VBH, fill: "url(#irr-grid)" }),

        (closed && points.length >= 3)
          ? React.createElement("polygon", {
              points: ptsAttr,
              fill: "rgba(56,189,248,0.22)",
              stroke: "rgb(56,189,248)", strokeWidth: 2,
              style: props.varDepth ? { pointerEvents: "none" } : null
            })
          : React.createElement("polyline", {
              points: ptsAttr,
              fill: "none",
              stroke: "rgb(56,189,248)", strokeWidth: 2,
              style: props.varDepth ? { pointerEvents: "none" } : null
            }),

!closed ? points.map(function (p, i) {
  var color = i === 0 ? "rgb(34,197,94)" : "rgb(59,130,246)";
  return React.createElement("circle", {
    key: i, cx: p.x, cy: p.y, r: 4, fill: color,
    style: props.varDepth ? { pointerEvents: "none" } : null
  });
}) : null,
// First calibration point marker (when calibA is set but calibB is not)
(calibMode && calibA && !calibB && !calibDragging)
  ? React.createElement("circle", {
      cx: calibA.x, cy: calibA.y, r: 4,
      fill: "rgb(234,88,12)", stroke: "#fff", strokeWidth: 2,
      style: { pointerEvents: "none" }
    })
  : null,
        (calibMode && calibFirst && calibHover)
          ? React.createElement("g", { style: { pointerEvents: "none" } },
              React.createElement("line", {
                x1: calibFirst.x, y1: calibFirst.y,
                x2: calibHover.x, y2: calibHover.y,
                stroke: "rgb(234,88,12)", strokeWidth: 2, strokeDasharray: "6 6"
              })
            )
          : null,

        (calibMode && promptOpen && calibA && calibB)
          ? (function(){
              var midX = (calibA.x + calibB.x) / 2;
              var midY = (calibA.y + calibB.y) / 2;
              var angRad = Math.atan2(calibB.y - calibA.y, calibB.x - calibA.x);
              var angDeg = angRad * 180 / Math.PI;
              if (Math.cos(angRad) < 0) angDeg += 180;

              return React.createElement("g", { style: { pointerEvents: "none" } },
                React.createElement("line", {
                  x1: calibA.x, y1: calibA.y,
                  x2: calibB.x, y2: calibB.y,
                  stroke: "rgb(234,88,12)", strokeWidth: 2
                }),
                React.createElement("g", { transform: "translate(" + midX + "," + midY + ") rotate(" + angDeg + ")" },
                  React.createElement("text", {
                    x: 0, y: -8, fontSize: 12, textAnchor: "middle",
                    fill: "rgb(234,88,12)", style: { fontWeight: 600 }
                  }, "Enter Distance Above")
                )
              );
            })()
          : null,

        // Dynamic dimensions (when closed and calibrated)
        (closed && points.length >= 3 && ftPerPx > 0 && ftPerPx !== 0.1)
          ? (function(){
              var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
              for (var i = 0; i < points.length; i++) {
                if (points[i].x < minX) minX = points[i].x;
                if (points[i].x > maxX) maxX = points[i].x;
                if (points[i].y < minY) minY = points[i].y;
                if (points[i].y > maxY) maxY = points[i].y;
              }
              
              var widthPx = maxX - minX;
              var heightPx = maxY - minY;
              var widthFt = widthPx * ftPerPx;
              var heightFt = heightPx * ftPerPx;
              
              var dimOffset = 16;
              var leftLineX = minX - dimOffset;
              var bottomLineY = maxY + dimOffset;
              
              return React.createElement("g", { style: { pointerEvents: "none" } },
                React.createElement("g", null,
                  React.createElement("line", {
                    x1: leftLineX, y1: minY,
                    x2: leftLineX, y2: maxY,
                    stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25
                  }),
                  React.createElement("line", { x1: leftLineX - 4, y1: minY, x2: leftLineX + 4, y2: minY, stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25 }),
                  React.createElement("line", { x1: leftLineX - 4, y1: maxY, x2: leftLineX + 4, y2: maxY, stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25 }),
                  React.createElement("text", {
                    x: leftLineX - 8, y: (minY + maxY) / 2,
                    fontSize: 11, textAnchor: "middle", fill: "rgba(31,41,55,.9)",
                    transform: "rotate(-90 " + (leftLineX - 8) + " " + ((minY + maxY) / 2) + ")",
                    style: { fontWeight: 600 }
                  }, formatFtIn(heightFt))
                ),
                
                React.createElement("g", null,
                  React.createElement("line", {
                    x1: minX, y1: bottomLineY,
                    x2: maxX, y2: bottomLineY,
                    stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25
                  }),
                  React.createElement("line", { x1: minX, y1: bottomLineY - 4, x2: minX, y2: bottomLineY + 4, stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25 }),
                  React.createElement("line", { x1: maxX, y1: bottomLineY - 4, x2: maxX, y2: bottomLineY + 4, stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25 }),
                  React.createElement("text", {
                    x: (minX + maxX) / 2, y: bottomLineY + 14,
                    fontSize: 11, textAnchor: "middle", fill: "rgba(31,41,55,.9)",
                    style: { fontWeight: 600 }
                  }, formatFtIn(widthFt))
                )
              );
            })()
          : null,

        (props.varDepth && ftPerPx > 0)
          ? h(DepthGridOverlay, {
              pts: points, closed: closed, ftPerPx: ftPerPx,
              cellFt: props.gridCellFt,
              depths: props.gridDepths, setDepths: props.setGridDepths,
              brush: props.gridBrush, setBrush: props.setGridBrush,
              baseDepth: props.baseDepth || "0"
            })
          : null
      )
    ),

    // Actions (BELOW SVG)
    React.createElement("div", { className: "pik-toolbar", style: { marginTop: 8, flexWrap: "wrap" } },
      !props.varDepth ? React.createElement(React.Fragment, null,
        React.createElement("button", {
          type: "button", className: "pik-btn ghost",
          onClick: function () { setClosed(false); }, disabled: points.length < 3 || !closed
        }, "Reopen"),
        React.createElement("button", {
          type: "button", className: "pik-btn subtle",
          onClick: closePoly, disabled: closed || points.length < 3
        }, "Close shape"),
        React.createElement("button", {
          type: "button", className: "pik-btn subtle",
          onClick: undo, disabled: points.length === 0
        }, "Undo"),
    // Only show Erase button when shape is NOT closed (points are visible)
    !closed ? React.createElement("button", {
      type: "button",
      className: "pik-btn subtle",
      "aria-pressed": eraseMode ? "true" : "false",
      onClick: function(){ setEraseMode(!eraseMode); setIsErasing(false); setCalibMode(false); }
    }, eraseMode ? "Erasing…" : "Erase") : null,
        (eraseMode
          ? React.createElement("span", {
              className: "pik-hint",
              style: { marginLeft: 8 }
            }, "Click or drag to remove points.")
          : null)
      ) : null,
        // Clear button moved to end
  React.createElement("button", {
    type: "button", className: "pik-btn danger",
    onClick: clearAll, disabled: points.length === 0,
    style: { marginLeft: "auto" }
  }, "Clear")
    )
  );
}

/* ===== [PIK] DepthGridOverlay START (stable hooks) ===== */
// Draws and paints depths INSIDE the IrregularCanvas <svg> (same window)
function DepthGridOverlay(props) {
  var pts = props.pts, closed = props.closed, ftPerPx = props.ftPerPx;
  var cellFt = props.cellFt;  // desired square cell size in feet
  var depths = props.depths, setDepths = props.setDepths;
  var brush = props.brush;
  var baseDepth = props.baseDepth;

  // --- Hooks: keep order and count constant every render ---
  var paintingRef = React.useRef(false);
  var clipIdRef = React.useRef("clip-depth-" + Math.random().toString(36).slice(2));
  var containerRef = React.useRef(null);

  React.useEffect(function () {
    function stop(){ paintingRef.current = false; }
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    return function(){
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
  }, []);
  // --- End of hooks. No returns above this line ---

  // Guard flags (computed after hooks so hook order never changes)
  var VBW = 560, VBH = 360;
  var canDraw = !!closed && Array.isArray(pts) && pts.length >= 3 && ftPerPx > 0;

  // Derive grid dims (no hooks needed)
  var safeCellFt = Math.max(0.5, +cellFt || 1);
  var px = (ftPerPx > 0 && safeCellFt > 0) ? (safeCellFt / ftPerPx) : 40;
  var nx = Math.max(8, Math.ceil(VBW / px));
  var ny = Math.max(8, Math.ceil(VBH / px));
  var cellPx = px;

  // Initialize depth array length when needed (run via setDepths, not a hook)
  if (canDraw) {
    var need = nx * ny;
    if (!Array.isArray(depths) || depths.length !== need) {
      var init = new Array(need);
      var d0 = Math.max(0, parseFloat(baseDepth) || 0);
      for (var i=0;i<need;i++) init[i] = d0;
      // queue state update
      setDepths(init);
      // don’t try to render cells until next frame when size matches
      return null;
    }
  }

  // After all guards, if we still can’t draw, render nothing (safe now)
  if (!canDraw) return null;

  // Helpers
  function idx(ix, iy){ return iy * nx + ix; }
  function setCell(ix, iy, d){
    if (ix<0 || iy<0 || ix>=nx || iy>=ny) return;
    setDepths(function(arr){
      if (!arr || arr.length !== nx*ny) return arr;
      var next = arr.slice();
      next[idx(ix,iy)] = Math.max(0, parseFloat(d) || 0);
      return next;
    });
  }

  // Color stats (no hooks)
  var maxDepth = Math.max(
    0,
    (Array.isArray(depths) ? depths.reduce(function(m,v){
      var n = +v; return Number.isFinite(n) && n>m ? n : m;
    }, 0) : 0),
    Math.max(0, +baseDepth || 0)
  );

  // Pointer handlers on cells
function getCellFromPoint(clientX, clientY) {
  if (!containerRef.current) return null;
  var svg = containerRef.current.ownerSVGElement;
  if (!svg) return null;
  
  var pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  var svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
  
  var ix = Math.floor(svgP.x / cellPx);
  var iy = Math.floor(svgP.y / cellPx);
  
  if (ix >= 0 && ix < nx && iy >= 0 && iy < ny) {
    return { ix: ix, iy: iy };
  }
  return null;
}

function onPointerDown(e){
  if (!closed) return;
  e.preventDefault();
  paintingRef.current = true;
  var cell = getCellFromPoint(e.clientX, e.clientY);
  if (cell) setCell(cell.ix, cell.iy, brush);
}

function onPointerMove(e){
  if (!paintingRef.current) return;
  e.preventDefault();
  var cell = getCellFromPoint(e.clientX, e.clientY);
  if (cell) setCell(cell.ix, cell.iy, brush);
}

function onPointerUp(e){ 
  paintingRef.current = false; 
}

  // Build cells
  var cells = [];
  for (var iy=0; iy<ny; iy++){
    for (var ix=0; ix<nx; ix++){
      var i = idx(ix,iy);
      cells.push(
        h("rect", {
          key: i,
          x: ix*cellPx, y: iy*cellPx,
          width: cellPx, height: cellPx,
          "data-ix": ix, "data-iy": iy,
          fill: depthColor(depths[i], maxDepth),
          stroke: "rgba(17,24,39,.08)", strokeWidth: 1          
        })
      );
    }
  }

  var clipId = clipIdRef.current;

  return h(React.Fragment, null,
    h("defs", null,
      h("clipPath", { id: clipId },
        h("polygon", { points: pts.map(function(p){return p.x+","+p.y;}).join(" ") })
      )
    ),
    h("g", { clipPath: "url(#" + clipId + ")",
    ref: containerRef,
    onPointerDown: onPointerDown,
    onPointerMove: onPointerMove,
    onPointerUp: onPointerUp,
    onPointerLeave: onPointerUp,
    style: { touchAction: "none" }
     }, cells)
  );
}
/* ===== [PIK] DepthGridOverlay END (stable hooks) ===== */
function DepthGridControls(props){
  // expects: dep, gridBrush, setGridBrush, gridCellFt, setGridCellFt, setGridDepths, ftPerPx
  var ftPerPx = props.ftPerPx || 0.1; // fallback for safety
  
  return React.createElement("div", { className:"pik-row" },
    React.createElement("div", { className:"pik-help" },
      React.createElement("div", { className:"pik-row pik-row--pair" },
        React.createElement("label", null,
          React.createElement("span", { className:"pik-label-title" }, "Brush depth (ft)"),
          React.createElement("input", {
            className:"pik-input", type:"number", min:"0", step:"0.1",
            value: props.gridBrush, onChange: function(e){ props.setGridBrush(e.target.value); }
          }),
          React.createElement("div", { className:"pik-hint" }, "Click/drag inside the shape to paint this depth.")
        ),
        h("label", null,
          h("span", { className:"pik-label-title" }, "Cell size"),
          h("select", {
            className:"pik-select",
            value: props.gridCellFt.toString(),
            onChange: function(e){
              var v = parseFloat(e.target.value);
              
              // Check if user has painted any custom depths
              var hasPainted = false;
              var avgDepth = Math.max(0, parseFloat(props.dep) || 0);
              if (Array.isArray(props.gridDepths)) {
                for (var i = 0; i < props.gridDepths.length; i++) {
                  if (Math.abs(props.gridDepths[i] - avgDepth) > 0.01) {
                    hasPainted = true;
                    break;
                  }
                }
              }
              
              if (hasPainted) {
                if (!confirm("Changing cell size will erase your painted depths. Continue?")) {
                  return; // user cancelled
                }
              }
              
              props.setGridCellFt(v);
              
              var VBW = 560, VBH = 360;
              // USE ACTUAL ftPerPx FROM PARENT COMPONENT (CRITICAL FIX)
              var px = (ftPerPx > 0 && v > 0) ? (v / ftPerPx) : 40;
              var newNx = Math.max(8, Math.ceil(VBW / px));
              var newNy = Math.max(8, Math.ceil(VBH / px));
              var newSize = newNx * newNy;
              var d0 = Math.max(0, parseFloat(props.dep) || 0);
              var newArr = new Array(newSize);
              for (var i = 0; i < newSize; i++) newArr[i] = d0;
              props.setGridDepths(newArr);
            }
          },
            h("option", {value:"0.5"}, "6 inches - High precision"),
            h("option", {value:"1"}, "1 foot - Standard"),
            h("option", {value:"2"}, "2 feet - Medium ponds"),
            h("option", {value:"5"}, "5 feet - Large ponds")
          ),
          h("div", { className:"pik-hint" }, "Smaller cells give finer depth control. Use 6\" for shelves, 5ft for large ponds.")
        )
      ),
      React.createElement("div", { className:"pik-toolbar", style:{ marginTop:6 } },
        React.createElement("button", {
          type:"button", className:"pik-btn subtle",
          onClick: function(){
            var d0 = Math.max(0, parseFloat(props.dep)||0);
            props.setGridDepths(function(arr){
              var nxny = (arr && arr.length) ? arr.length : 0;
              var next = new Array(nxny || 0);
              for (var i=0;i<(nxny||0);i++) next[i] = d0;
              return next;
            });
          }
        }, (function(){
          var v = Math.max(0, parseFloat(props.dep)||0);
          return v > 0 ? "Reset to average depth of " + fmt(v, 2) + " ft" : "Reset to average depth";
        })())
      )
    )
  );
}


// ========== REFACTORED SHAPE CANVAS ARCHITECTURE ==========
// Shared sub-components and base canvas with config injection

// ---------- Shared Sub-Component: Depth Painting Toggle ----------
function DepthPaintingToggle(props) {
  return h("div", { className: "pik-row", style: { marginBottom: 8 } },
    h("div", {
      className: "pik-toolbar",
      style: { display: "flex", alignItems: "center", gap: 8, width: "100%" }
    },
      h("button", {
        type: "button",
        className: "pik-btn",
        "aria-pressed": props.varDepth ? "true" : "false",
        style: props.varDepth
          ? { background: "rgb(56,189,248)", color: "#fff", border: "1px solid rgb(56,189,248)" }
          : { background: "#FFE219", color: "rgb(31,41,55)", border: "1px solid #FFE219" },
        onClick: function(){ if (props.setVarDepth) props.setVarDepth(!props.varDepth); }
      }, props.varDepth ? "Depth painting: ON" : "Depth painting: OFF")
    )
  );
}

// ---------- Shared Sub-Component: Shape Handle (3-circle pattern) ----------
function ShapeHandle(props) {
  return h("g", { key: props.id },
    // Invisible larger touch target (22px radius = 44px diameter)
    h("circle", {
      ref: props.innerRef,
      cx: props.x, cy: props.y, r: 22,
      fill: "transparent",
      stroke: "none",
      style: { cursor: props.cursor }
    }),
    // Visible smaller handle (white ring)
    h("circle", {
      cx: props.x, cy: props.y, r: 6,
      fill: "#fff",
      stroke: "rgba(31,41,55,.25)",
      strokeWidth: 2,
      style: { pointerEvents: "none" }
    }),
    // Inner dot
    h("circle", {
      cx: props.x, cy: props.y, r: 3,
      fill: "rgb(56,189,248)",
      style: { pointerEvents: "none" }
    })
  );
}

// ---------- Shared Sub-Component: Dimension Line ----------
function DimensionLine(props) {
  var mk = props.markerId;
  var x1 = props.x1, y1 = props.y1, x2 = props.x2, y2 = props.y2;
  var textX = props.textX, textY = props.textY;
  var label = props.label, value = props.value;
  var textAnchor = props.textAnchor || "middle";

  return h(React.Fragment, null,
    h("path", {
      d: "M " + x1 + " " + y1 + " L " + x2 + " " + y2,
      stroke: "rgba(31,41,55,.8)", strokeWidth: 1.25,
      markerStart: "url(#" + mk + ")", markerEnd: "url(#" + mk + ")", fill: "none"
    }),
    h("text", {
      x: textX, y: textY, fontSize: 12, textAnchor: textAnchor,
      fill: "rgba(31,41,55,.9)", style: { fontWeight: 600 }
    },
      h("tspan", { x: textX, dy: 0 }, label + ": " + formatFtIn(value)),
      h("tspan", { x: textX, dy: 14, className: "pik-subtle" }, "(" + fmt(value, 2) + " ft)")
    )
  );
}

// ---------- Base Shape Canvas Component ----------
function BaseShapeCanvas(props) {
  var config = props.shapeConfig;

  // Constants (shared across all shapes)
  var VBW = 560, VBH = 360;
  var PAD = 8, GAP = 6, LABEL_W = 140, LABEL_H = 28, LINE_OUT_X = 24, LINE_OUT_Y = 16;
  var MARG = { left: 24, top: 24, right: PAD + LINE_OUT_X + GAP + LABEL_W, bottom: PAD + LINE_OUT_Y + GAP + LABEL_H };
  var GRID_PX = 40;

  // Get dimensions from config
  var dims = config.getDimensions(props);
  var dimRefs = config.createDimRefs(dims);

  // Update refs when dimensions change
  config.updateDimRefs(dimRefs, props);

  // Calculate layout
  var usableW = Math.max(40, VBW - (MARG.left + MARG.right));
  var usableH = Math.max(40, VBH - (MARG.top + MARG.bottom));
  var pxPerFt = config.calculateScale(dims, usableW, usableH);
  pxPerFt = Math.max(4, Math.min(40, pxPerFt));
  var ftPerPx = 1 / pxPerFt;

  // Calculate bounds and handles
  var bounds = config.calculateBounds(dims, pxPerFt, MARG, VBW, VBH);
  var handles = config.getHandles(bounds);

  // Setup refs for interact.js
  var scaleRef = React.useRef(ftPerPx);
  scaleRef.current = ftPerPx;
  var uid = React.useMemo(function(){ return Math.random().toString(36).slice(2); }, []);
  var mk = "arrow-" + config.name + "-" + uid;

  var handleRefs = React.useRef({});
  var detachFnsRef = React.useRef([]);

  // Attach interact.js handlers (disabled when depth painting is on)
  React.useEffect(function(){
    // Skip attaching handlers when depth painting mode is active
    if (props.varDepth) {
      return function(){}; // No-op cleanup
    }

    if (!window.interact) {
      var t = setTimeout(function(){}, 120);
      return function(){ clearTimeout(t); };
    }

    handles.forEach(function(h){
      var el = handleRefs.current[h.id];
      if (!el) return;

      var detach = InteractAdapter.attach(el, h.id, {
        onMove: function(e){
          var dftX = e.dx * scaleRef.current;
          var dftY = e.dy * scaleRef.current;
          config.onHandleDrag(h.id, dftX, dftY, dimRefs, props);
        }
      }, config.getInteractOptions ? config.getInteractOptions(ftPerPx) : { snapFt: 0, ftPerPx: ftPerPx });

      detachFnsRef.current.push(detach);
    });

    return function(){
      detachFnsRef.current.forEach(function(fn){ try{ fn(); }catch(e){} });
      detachFnsRef.current = [];
    };
  }, [props.varDepth]);

  // Get clip points for depth overlay
  var clipPoints = config.getClipPoints(bounds);
  var showDepthOverlay = props.varDepth && config.shouldShowDepthOverlay(dims);

  return h("div", { className: "pik-card", style: { paddingBottom: 10 } },
    h("div", { className: "pik-title", style: { marginBottom: 8 } }, "Shape"),

    // Depth painting toggle
    h(DepthPaintingToggle, {
      varDepth: props.varDepth,
      setVarDepth: props.setVarDepth
    }),

    // Depth grid controls (when painting is ON)
    props.varDepth
      ? h("div", { className: "pik-card pik-shimmer", style: { marginBottom: 8, borderColor: "#FFE219" } },
          h(DepthGridControls, {
            dep: props.baseDepth,
            gridBrush: props.gridBrush, setGridBrush: props.setGridBrush,
            gridCellFt: props.gridCellFt, setGridCellFt: props.setGridCellFt,
            setGridDepths: props.setGridDepths,
            ftPerPx: ftPerPx,
            gridDepths: props.gridDepths
          })
        )
      : null,

    h("div", { className: "pik-subtle", style: { marginBottom: 8 } }, config.helpText),

    // SVG Canvas
    h("div", {
      style: {
        position: "relative",
        border: "1px solid var(--pik-border,#e5e7eb)",
        borderRadius: "10px",
        overflow: "hidden"
      }
    },
      h("svg", {
        width: "100%",
        viewBox: "0 0 " + VBW + " " + VBH,
        style: {
          display: "block",
          background: "rgba(0,0,0,0.02)",
          userSelect: "none",
          touchAction: "none",
          cursor: props.varDepth ? "crosshair" : "default"
        }
      },
        h("defs", null,
          h("pattern", { id: config.name + "-grid-" + uid, width: GRID_PX, height: GRID_PX, patternUnits: "userSpaceOnUse" },
            h("path", { d: "M " + GRID_PX + " 0 L 0 0 0 " + GRID_PX, fill: "none", stroke: "rgba(0,0,0,0.06)", strokeWidth: 1 })
          ),
          h("marker", {
            id: mk, markerWidth: 8, markerHeight: 8, refX: 6, refY: 4, orient: "auto-start-reverse"
          }, h("path", { d: "M 0 0 L 8 4 L 0 8 z", fill: "rgba(31,41,55,.8)" }))
        ),
        h("rect", { width: VBW, height: VBH, fill: "url(#" + config.name + "-grid-" + uid + ")" }),

        // Shape-specific rendering
        config.renderShape(bounds, dims),

        // Depth overlay (when ON)
        showDepthOverlay
          ? h(DepthGridOverlay, {
              pts: clipPoints,
              closed: true,
              ftPerPx: ftPerPx,
              cellFt: props.gridCellFt,
              depths: props.gridDepths, setDepths: props.setGridDepths,
              brush: props.gridBrush, setBrush: props.setGridBrush,
              baseDepth: props.baseDepth || "0"
            })
          : null,

        // Dimension lines
        config.renderDimensions(bounds, dims, mk, LINE_OUT_X, LINE_OUT_Y, GAP),

        // Handles (hidden when depth painting is on)
        !props.varDepth ? handles.map(function(handle){
          return h(ShapeHandle, {
            key: handle.id,
            id: handle.id,
            x: handle.x,
            y: handle.y,
            cursor: handle.cursor,
            innerRef: function(node){ handleRefs.current[handle.id] = node; }
          });
        }) : null
      )
    )
  );
}

// ---------- Shape Configuration: Rectangle ----------
var RectangleConfig = {
  name: "rect",
  helpText: "Drag handles to adjust dimensions.",

  getDimensions: function(props) {
    return {
      width: Math.max(0, parseFloat(props.wid) || 0),
      length: Math.max(0, parseFloat(props.len) || 0)
    };
  },

  createDimRefs: function(dims) {
    return {
      wRef: React.useRef(dims.width),
      lRef: React.useRef(dims.length)
    };
  },

  updateDimRefs: function(refs, props) {
    React.useEffect(function(){ refs.wRef.current = Math.max(0, parseFloat(props.wid) || 0); }, [props.wid]);
    React.useEffect(function(){ refs.lRef.current = Math.max(0, parseFloat(props.len) || 0); }, [props.len]);
  },

  calculateScale: function(dims, usableW, usableH) {
    if (dims.length > 0 || dims.width > 0) {
      var sX = (dims.width > 0) ? (usableW / Math.max(dims.width, 0.0001)) : Infinity;
      var sY = (dims.length > 0) ? (usableH / Math.max(dims.length, 0.0001)) : Infinity;
      return Math.min(sX, sY);
    }
    return 10;
  },

  calculateBounds: function(dims, pxPerFt, MARG, VBW, VBH) {
    var pxW = Math.max(0, dims.width * pxPerFt);
    var pxL = Math.max(0, dims.length * pxPerFt);

    var neededW = MARG.left + pxW + MARG.right;
    var neededH = MARG.top + pxL + MARG.bottom;
    var offsetX = Math.max(0, (VBW - neededW) / 2);
    var offsetY = Math.max(0, (VBH - neededH) / 2);

    var x0 = MARG.left + offsetX, x1 = x0 + pxW;
    var y0 = MARG.top + offsetY, y1 = y0 + pxL;
    var cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;

    return { x0: x0, y0: y0, x1: x1, y1: y1, cx: cx, cy: cy, width: pxW, height: pxL };
  },

  getHandles: function(bounds) {
    return [
      { id: "E", x: bounds.x1, y: bounds.cy, cursor: "ew-resize" },
      { id: "W", x: bounds.x0, y: bounds.cy, cursor: "ew-resize" },
      { id: "N", x: bounds.cx, y: bounds.y0, cursor: "ns-resize" },
      { id: "S", x: bounds.cx, y: bounds.y1, cursor: "ns-resize" }
    ];
  },

  renderShape: function(bounds, dims) {
    return h("rect", {
      x: bounds.x0, y: bounds.y0,
      width: Math.max(0, bounds.x1 - bounds.x0),
      height: Math.max(0, bounds.y1 - bounds.y0),
      fill: "rgba(56,189,248,0.22)",
      stroke: "rgb(56,189,248)",
      strokeWidth: 2
    });
  },

  renderDimensions: function(bounds, dims, mk, LINE_OUT_X, LINE_OUT_Y, GAP) {
    return [
      // Width dimension (bottom)
      h(DimensionLine, {
        key: "width",
        markerId: mk,
        x1: bounds.x0, y1: bounds.y1 + LINE_OUT_Y,
        x2: bounds.x1, y2: bounds.y1 + LINE_OUT_Y,
        textX: bounds.cx, textY: bounds.y1 + LINE_OUT_Y + GAP + 16,
        textAnchor: "middle",
        label: "Width",
        value: dims.width
      }),
      // Length dimension (right)
      h(DimensionLine, {
        key: "length",
        markerId: mk,
        x1: bounds.x1 + LINE_OUT_X, y1: bounds.y0,
        x2: bounds.x1 + LINE_OUT_X, y2: bounds.y1,
        textX: bounds.x1 + LINE_OUT_X + GAP, textY: bounds.cy,
        textAnchor: "start",
        label: "Length",
        value: dims.length
      })
    ];
  },

  onHandleDrag: function(handleId, dftX, dftY, dimRefs, props) {
    if (handleId === "E" || handleId === "W") {
      var hwFt = (dimRefs.wRef.current || 0) / 2;
      var sign = (handleId === "E") ? +1 : -1;
      var nextHW = Math.max(0, hwFt + sign * dftX);
      var nextW = Math.max(0, nextHW * 2);
      nextW = snapToInches(nextW, 1);
      props.setWid(ftToInput(nextW));
    } else {
      var hlFt = (dimRefs.lRef.current || 0) / 2;
      var signY = (handleId === "S") ? +1 : -1;
      var nextHL = Math.max(0, hlFt + signY * dftY);
      var nextL = Math.max(0, nextHL * 2);
      nextL = snapToInches(nextL, 1);
      props.setLen(ftToInput(nextL));
    }
  },

  getClipPoints: function(bounds) {
    return makeRectPtsPx(bounds.x0, bounds.y0, bounds.x1, bounds.y1);
  },

  shouldShowDepthOverlay: function(dims) {
    return dims.length > 0 && dims.width > 0;
  },

  getInteractOptions: function(ftPerPx) {
    return { snapFt: 0, ftPerPx: ftPerPx };
  }
};

// ---------- Shape Configuration: Circle ----------
var CircleConfig = {
  name: "circle",
  helpText: "Drag handles to adjust diameter.",

  getDimensions: function(props) {
    return {
      diameter: Math.max(0, parseFloat(props.diam) || 0)
    };
  },

  createDimRefs: function(dims) {
    return {
      dRef: React.useRef(dims.diameter)
    };
  },

  updateDimRefs: function(refs, props) {
    React.useEffect(function(){ refs.dRef.current = Math.max(0, parseFloat(props.diam) || 0); }, [props.diam]);
  },

  calculateScale: function(dims, usableW, usableH) {
    return (dims.diameter > 0) ? Math.min(usableW, usableH) / dims.diameter : 10;
  },

  calculateBounds: function(dims, pxPerFt, MARG, VBW, VBH) {
    var pxD = Math.max(0, dims.diameter * pxPerFt);
    var neededW = MARG.left + pxD + MARG.right;
    var neededH = MARG.top + pxD + MARG.bottom;
    var offsetX = Math.max(0, (VBW - neededW) / 2);
    var offsetY = Math.max(0, (VBH - neededH) / 2);

    var x0 = MARG.left + offsetX, x1 = x0 + pxD;
    var y0 = MARG.top + offsetY, y1 = y0 + pxD;
    var cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    var r = pxD / 2;

    return { x0: x0, y0: y0, x1: x1, y1: y1, cx: cx, cy: cy, r: r };
  },

  getHandles: function(bounds) {
    return [
      { id: "E", x: bounds.x1, y: bounds.cy, cursor: "ew-resize" },
      { id: "W", x: bounds.x0, y: bounds.cy, cursor: "ew-resize" }
    ];
  },

  renderShape: function(bounds, dims) {
    return h("circle", {
      cx: bounds.cx, cy: bounds.cy, r: bounds.r,
      fill: "rgba(56,189,248,0.22)",
      stroke: "rgb(56,189,248)",
      strokeWidth: 2
    });
  },

  renderDimensions: function(bounds, dims, mk, LINE_OUT_X, LINE_OUT_Y, GAP) {
    return [
      // Diameter dimension (bottom)
      h(DimensionLine, {
        key: "diameter",
        markerId: mk,
        x1: bounds.x0, y1: bounds.y1 + LINE_OUT_Y,
        x2: bounds.x1, y2: bounds.y1 + LINE_OUT_Y,
        textX: bounds.cx, textY: bounds.y1 + LINE_OUT_Y + GAP + 16,
        textAnchor: "middle",
        label: "Diameter",
        value: dims.diameter
      })
    ];
  },

  onHandleDrag: function(handleId, dftX, dftY, dimRefs, props) {
    var halfD = (dimRefs.dRef.current || 0) / 2;
    var sign = (handleId === "E") ? +1 : -1;
    var nextHD = Math.max(0, halfD + sign * dftX);
    var nextD = Math.max(0, nextHD * 2);
    nextD = snapToInches(nextD, 1);
    props.setDiam(ftToInput(nextD));
  },

  getClipPoints: function(bounds) {
    return makeEllipsePtsPx(bounds.cx, bounds.cy, bounds.r, bounds.r, 72);
  },

  shouldShowDepthOverlay: function(dims) {
    return dims.diameter > 0;
  }
};

// ---------- Shape Configuration: Oval ----------
var OvalConfig = {
  name: "oval",
  helpText: "Drag handles to adjust axes.",

  getDimensions: function(props) {
    return {
      axisA: Math.max(0, parseFloat(props.axisA) || 0),
      axisB: Math.max(0, parseFloat(props.axisB) || 0)
    };
  },

  createDimRefs: function(dims) {
    return {
      aRef: React.useRef(dims.axisA),
      bRef: React.useRef(dims.axisB)
    };
  },

  updateDimRefs: function(refs, props) {
    React.useEffect(function(){ refs.aRef.current = Math.max(0, parseFloat(props.axisA) || 0); }, [props.axisA]);
    React.useEffect(function(){ refs.bRef.current = Math.max(0, parseFloat(props.axisB) || 0); }, [props.axisB]);
  },

  calculateScale: function(dims, usableW, usableH) {
    return (dims.axisA > 0 && dims.axisB > 0)
      ? Math.min(usableW / dims.axisA, usableH / dims.axisB)
      : 10;
  },

  calculateBounds: function(dims, pxPerFt, MARG, VBW, VBH) {
    var pxW = Math.max(0, dims.axisA * pxPerFt);
    var pxL = Math.max(0, dims.axisB * pxPerFt);

    var neededW = MARG.left + pxW + MARG.right;
    var neededH = MARG.top + pxL + MARG.bottom;
    var offsetX = Math.max(0, (VBW - neededW) / 2);
    var offsetY = Math.max(0, (VBH - neededH) / 2);

    var x0 = MARG.left + offsetX, x1 = x0 + pxW;
    var y0 = MARG.top + offsetY, y1 = y0 + pxL;
    var cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    var rx = pxW / 2, ry = pxL / 2;

    return { x0: x0, y0: y0, x1: x1, y1: y1, cx: cx, cy: cy, rx: rx, ry: ry };
  },

  getHandles: function(bounds) {
    return [
      { id: "E", x: bounds.x1, y: bounds.cy, cursor: "ew-resize" },
      { id: "W", x: bounds.x0, y: bounds.cy, cursor: "ew-resize" },
      { id: "N", x: bounds.cx, y: bounds.y0, cursor: "ns-resize" },
      { id: "S", x: bounds.cx, y: bounds.y1, cursor: "ns-resize" }
    ];
  },

  renderShape: function(bounds, dims) {
    return h("ellipse", {
      cx: bounds.cx, cy: bounds.cy, rx: bounds.rx, ry: bounds.ry,
      fill: "rgba(56,189,248,0.22)",
      stroke: "rgb(56,189,248)",
      strokeWidth: 2
    });
  },

  renderDimensions: function(bounds, dims, mk, LINE_OUT_X, LINE_OUT_Y, GAP) {
    return [
      // Major axis (bottom)
      h(DimensionLine, {
        key: "axisA",
        markerId: mk,
        x1: bounds.x0, y1: bounds.y1 + LINE_OUT_Y,
        x2: bounds.x1, y2: bounds.y1 + LINE_OUT_Y,
        textX: bounds.cx, textY: bounds.y1 + LINE_OUT_Y + GAP + 16,
        textAnchor: "middle",
        label: "Major axis",
        value: dims.axisA
      }),
      // Minor axis (right)
      h(DimensionLine, {
        key: "axisB",
        markerId: mk,
        x1: bounds.x1 + LINE_OUT_X, y1: bounds.y0,
        x2: bounds.x1 + LINE_OUT_X, y2: bounds.y1,
        textX: bounds.x1 + LINE_OUT_X + GAP, textY: bounds.cy,
        textAnchor: "start",
        label: "Minor axis",
        value: dims.axisB
      })
    ];
  },

  onHandleDrag: function(handleId, dftX, dftY, dimRefs, props) {
    if (handleId === "E" || handleId === "W") {
      var halfA = (dimRefs.aRef.current || 0) / 2;
      var sign = (handleId === "E") ? +1 : -1;
      var nextA = Math.max(0, (halfA + sign * dftX) * 2);
      nextA = snapToInches(nextA, 1);
      props.setAxisA(ftToInput(nextA));
    } else {
      var halfB = (dimRefs.bRef.current || 0) / 2;
      var signY = (handleId === "S") ? +1 : -1;
      var nextB = Math.max(0, (halfB + signY * dftY) * 2);
      nextB = snapToInches(nextB, 1);
      props.setAxisB(ftToInput(nextB));
    }
  },

  getClipPoints: function(bounds) {
    return makeEllipsePtsPx(bounds.cx, bounds.cy, bounds.rx, bounds.ry, 72);
  },

  shouldShowDepthOverlay: function(dims) {
    return dims.axisA > 0 && dims.axisB > 0;
  }
};

// ---------- Shape Configuration: L-Shape ----------
var LShapeConfig = {
  name: "lshape",
  helpText: "Drag handles to adjust. Drag the cutout corner to resize the notch.",

  getDimensions: function(props) {
    return {
      Lo: Math.max(0, parseFloat(props.Lo) || 0),
      Wo: Math.max(0, parseFloat(props.Wo) || 0),
      Li: Math.max(0, parseFloat(props.Li) || 0),
      Wi: Math.max(0, parseFloat(props.Wi) || 0)
    };
  },

  createDimRefs: function(dims) {
    return {
      loRef: React.useRef(dims.Lo),
      woRef: React.useRef(dims.Wo),
      liRef: React.useRef(dims.Li),
      wiRef: React.useRef(dims.Wi)
    };
  },

  updateDimRefs: function(refs, props) {
    React.useEffect(function(){ refs.loRef.current = Math.max(0, parseFloat(props.Lo) || 0); }, [props.Lo]);
    React.useEffect(function(){ refs.woRef.current = Math.max(0, parseFloat(props.Wo) || 0); }, [props.Wo]);
    React.useEffect(function(){ refs.liRef.current = Math.max(0, parseFloat(props.Li) || 0); }, [props.Li]);
    React.useEffect(function(){ refs.wiRef.current = Math.max(0, parseFloat(props.Wi) || 0); }, [props.Wi]);
  },

  calculateScale: function(dims, usableW, usableH) {
    if (dims.Lo > 0 && dims.Wo > 0) {
      var sX = usableW / Math.max(dims.Lo, 0.0001);
      var sY = usableH / Math.max(dims.Wo, 0.0001);
      return Math.min(sX, sY);
    }
    return 10;
  },

  calculateBounds: function(dims, pxPerFt, MARG, VBW, VBH) {
    var pxLo = Math.max(0, dims.Lo * pxPerFt);
    var pxWo = Math.max(0, dims.Wo * pxPerFt);
    var pxLi = Math.max(0, dims.Li * pxPerFt);
    var pxWi = Math.max(0, dims.Wi * pxPerFt);

    var neededW = MARG.left + pxLo + MARG.right;
    var neededH = MARG.top + pxWo + MARG.bottom;
    var offsetX = Math.max(0, (VBW - neededW) / 2);
    var offsetY = Math.max(0, (VBH - neededH) / 2);

    var x0 = MARG.left + offsetX, x1 = x0 + pxLo;
    var y0 = MARG.top + offsetY, y1 = y0 + pxWo;

    var cutX0 = x1 - pxLi;
    var cutY1 = y0 + pxWi;

    return {
      x0: x0, y0: y0, x1: x1, y1: y1,
      cutX0: cutX0, cutY1: cutY1,
      pxLi: pxLi, pxWi: pxWi
    };
  },

  getHandles: function(bounds) {
    return [
      { id: "outerR", x: bounds.x1, y: (bounds.y0 + bounds.y1) / 2, cursor: "ew-resize" },
      { id: "outerB", x: (bounds.x0 + bounds.x1) / 2, y: bounds.y1, cursor: "ns-resize" },
      { id: "cutCorner", x: bounds.cutX0, y: bounds.cutY1, cursor: "nwse-resize" }
    ];
  },

  renderShape: function(bounds, dims) {
    var clipPts = [
      {x: bounds.x0, y: bounds.y0},
      {x: bounds.cutX0, y: bounds.y0},
      {x: bounds.cutX0, y: bounds.cutY1},
      {x: bounds.x1, y: bounds.cutY1},
      {x: bounds.x1, y: bounds.y1},
      {x: bounds.x0, y: bounds.y1}
    ];

    return h("polygon", {
      points: clipPts.map(function(p){ return p.x + "," + p.y; }).join(" "),
      fill: "rgba(56,189,248,0.22)",
      stroke: "rgb(56,189,248)",
      strokeWidth: 2
    });
  },

  renderDimensions: function(bounds, dims, mk, LINE_OUT_X, LINE_OUT_Y, GAP) {
    var elements = [];

    // Outer length dimension (bottom)
    elements.push(
      h(DimensionLine, {
        key: "outerLength",
        markerId: mk,
        x1: bounds.x0, y1: bounds.y1 + LINE_OUT_Y,
        x2: bounds.x1, y2: bounds.y1 + LINE_OUT_Y,
        textX: (bounds.x0 + bounds.x1) / 2, textY: bounds.y1 + LINE_OUT_Y + GAP + 16,
        textAnchor: "middle",
        label: "Outer length",
        value: dims.Lo
      })
    );

    // Outer width dimension (right)
    elements.push(
      h(DimensionLine, {
        key: "outerWidth",
        markerId: mk,
        x1: bounds.x1 + LINE_OUT_X, y1: bounds.y0,
        x2: bounds.x1 + LINE_OUT_X, y2: bounds.y1,
        textX: bounds.x1 + LINE_OUT_X + GAP, textY: (bounds.y0 + bounds.y1) / 2,
        textAnchor: "start",
        label: "Outer width",
        value: dims.Wo
      })
    );

    // Cutout length dimension (smaller, inside)
    if (dims.Li > 2) {
      var lineY = bounds.cutY1 + 12;
      var textY = lineY + 12;
      elements.push(
        h(React.Fragment, { key: "cutLength" },
          h("path", {
            d: "M " + (bounds.cutX0 + 6) + " " + lineY + " L " + (bounds.x1 - 6) + " " + lineY,
            stroke: "rgba(148,163,184,.9)", strokeWidth: 1,
            markerStart: "url(#" + mk + ")", markerEnd: "url(#" + mk + ")", fill: "none"
          }),
          h("text", {
            x: (bounds.cutX0 + bounds.x1) / 2, y: textY, fontSize: 10, textAnchor: "middle",
            fill: "rgba(100,116,139,.95)", style: { fontWeight: 500 }
          }, "Length: " + formatFtIn(dims.Li))
        )
      );
    }

    // Cutout width dimension (smaller, inside)
    if (dims.Wi > 2) {
      var lineX = bounds.cutX0 - 12;
      var midY = (bounds.y0 + bounds.cutY1) / 2;
      elements.push(
        h(React.Fragment, { key: "cutWidth" },
          h("path", {
            d: "M " + lineX + " " + (bounds.y0 + 6) + " L " + lineX + " " + (bounds.cutY1 - 6),
            stroke: "rgba(148,163,184,.9)", strokeWidth: 1,
            markerStart: "url(#" + mk + ")", markerEnd: "url(#" + mk + ")", fill: "none"
          }),
          h("text", {
            x: lineX - 6, y: midY, fontSize: 10, textAnchor: "middle",
            fill: "rgba(100,116,139,.95)", style: { fontWeight: 500 },
            transform: "rotate(-90 " + (lineX - 6) + " " + midY + ")"
          }, "Width: " + formatFtIn(dims.Wi))
        )
      );
    }

    return elements;
  },

  onHandleDrag: function(handleId, dftX, dftY, dimRefs, props) {
    if (handleId === "outerR") {
      var nextLo = Math.max(0, dimRefs.loRef.current + dftX);
      nextLo = snapToInches(nextLo, 1);
      props.setLo(ftToInput(nextLo));
    } else if (handleId === "outerB") {
      var nextWo = Math.max(0, dimRefs.woRef.current + dftY);
      nextWo = snapToInches(nextWo, 1);
      props.setWo(ftToInput(nextWo));
    } else if (handleId === "cutCorner") {
      var nextLi = Math.max(0.5, Math.min(dimRefs.loRef.current - 1, dimRefs.liRef.current - dftX));
      var nextWi = Math.max(0.5, Math.min(dimRefs.woRef.current - 1, dimRefs.wiRef.current + dftY));

      nextLi = snapToInches(nextLi, 1);
      nextWi = snapToInches(nextWi, 1);
      props.setLi(ftToInput(nextLi));
      props.setWi(ftToInput(nextWi));
    }
  },

  getClipPoints: function(bounds) {
    return [
      {x: bounds.x0, y: bounds.y0},
      {x: bounds.cutX0, y: bounds.y0},
      {x: bounds.cutX0, y: bounds.cutY1},
      {x: bounds.x1, y: bounds.cutY1},
      {x: bounds.x1, y: bounds.y1},
      {x: bounds.x0, y: bounds.y1}
    ];
  },

  shouldShowDepthOverlay: function(dims) {
    return dims.Lo > 0 && dims.Wo > 0 && dims.Li < dims.Lo && dims.Wi < dims.Wo;
  }
};



  // ---------- Controls ----------
  function ControlsCard(props) {
    var s = props.state;
    return h("div", { className:"pik-card" },
      h("div", { className:"pik-title" }, "Controls"),
      h("div", { className:"pik-subtle", style:{ marginBottom:8 } }, "Choose a shape and enter dimensions. When drawing your pond, trace the waterline of the pond, then set a scale."),
        // Project Save/Load toolbar (appears for all shapes)
  h("div", { className:"pik-toolbar", style:{ marginBottom:12, paddingTop:8, borderTop:"1px solid var(--pik-border, #e5e7eb)" } },
    h(SaveProjectButton, {
      onSave: s.handleManualSave,
      onSaveSuccess: s.onSaveSuccess,
      onSaveDraft: s.handleSaveDraft
    }),
    h(SaveAsButton, {
      onSave: s.handleManualSave,
      onSaveDraft: s.handleSaveDraft
    }),
    h(ProjectSelectorDropdown, {
      onLoadProject: s.handleLoadProject
    }),
    // Active project indicator
    s.activeProject && h("div", { 
      className: "pond-project-status-compact",
      title: "Project: " + (s.activeProject.project.name || "Untitled"),
      style: { marginLeft: "auto" }
    },
      h("strong", null, s.activeProject.project.name || "Untitled"),
      s.lastSaved && h("span", { 
        className: "pond-status-saved",
        style: { marginLeft: 6, color: "#059669", fontSize: 12 }
      }, "✓ Saved")
    )
  ),
// Shape picker (NO depth field here anymore)
h("div", { className:"pik-row" },
  h("label", { htmlFor:"shape" },
    h("span", { className:"pik-label-title" }, "Shape"),
    h("select", { id:"shape", className:"pik-select", value:s.shape, onChange:function(e){ s.setShape(e.target.value); } },
      h("option",{value:"rectangle"},"Rectangle"),
      h("option",{value:"circle"},"Circle"),
      h("option",{value:"oval"},"Oval (Ellipse)"),
      h("option",{value:"lshape"},"L-Shape"),
      h("option",{value:"irregular"},"Draw your pond")
    ),
    h("div", { className:"pik-hint" }, s.shape==="irregular" ? "Trace your pond's shape, then calibrate scale for accuracy." : "Pick the closest match.")
  )
),


      // Shape-specific inputs
      (function(){
switch (s.shape){
case "rectangle":
  return h("div",{className:"pik-row"},
    // Global tip (once)
    h("div", { className:"pik-dimension-tip" }, 'Tip: Type 100", 8\'4", or 8.3333 (decimal feet).'),
    h("div",{className:"pik-row pik-row--pair"},
      h(FieldFeetInches, { id:"len", label:"Length", value:s.len, onChange:s.setLen, hideHint:true }),
      h(FieldFeetInches, { id:"wid", label:"Width",  value:s.wid, onChange:s.setWid, hideHint:true })
    ),
    h(BaseShapeCanvas, {
      shapeConfig: RectangleConfig,
      len: s.len, setLen: s.setLen,
      wid: s.wid, setWid: s.setWid,
      varDepth: s.varDepth, setVarDepth: s.setVarDepth,
      gridCellFt: s.gridCellFt, setGridCellFt: s.setGridCellFt,
      gridDepths: s.gridDepths, setGridDepths: s.setGridDepths,
      gridBrush: s.gridBrush, setGridBrush: s.setGridBrush,
      baseDepth: s.dep
    }),
    !s.varDepth ? h(FieldNumber, {
      id:"dep",
      label: "Average Depth (ft)",
      value: s.dep,
      onChange: s.setDep,
      step:"0.1",
      min:"0",
      hint: "Enter your pond's average depth, including shelves."
    }) : null
  );

case "circle":
  return h("div",{className:"pik-row"},
    h(FieldFeetInches, { id:"cd", label:"Diameter", value:s.circD, onChange:s.setCircD }),
    h(BaseShapeCanvas, {
      shapeConfig: CircleConfig,
      diam: s.circD, setDiam: s.setCircD,
      varDepth: s.varDepth, setVarDepth: s.setVarDepth,
      gridCellFt: s.gridCellFt,setGridCellFt: s.setGridCellFt,
      gridDepths: s.gridDepths, setGridDepths: s.setGridDepths,
      gridBrush: s.gridBrush, setGridBrush: s.setGridBrush,
      baseDepth: s.dep
    }),
    !s.varDepth ? h(FieldNumber, {
      id:"dep",
      label: "Average Depth (ft)",
      value: s.dep,
      onChange: s.setDep,
      step:"0.1",
      min:"0",
      hint: "Enter your pond's average depth, including shelves."
    }) : null
  );

case "oval":
  return h("div",{className:"pik-row"},
    h("div", { className:"pik-dimension-tip" }, 'Tip: Type 100", 8\'4", or 8.3333 (decimal feet).'),
    h("div",{className:"pik-row pik-row--pair"},
      h(FieldFeetInches, { id:"ovalA", label:"Major axis", value:s.ovalA, onChange:s.setOvalA, hideHint:true }),
      h(FieldFeetInches, { id:"ovalB", label:"Minor axis", value:s.ovalB, onChange:s.setOvalB, hideHint:true })
    ),
    h(BaseShapeCanvas, {
      shapeConfig: OvalConfig,
      axisA: s.ovalA, setAxisA: s.setOvalA,
      axisB: s.ovalB, setAxisB: s.setOvalB,
      varDepth: s.varDepth, setVarDepth: s.setVarDepth,
      gridCellFt: s.gridCellFt,setGridCellFt: s.setGridCellFt,
      gridDepths: s.gridDepths, setGridDepths: s.setGridDepths,
      gridBrush: s.gridBrush, setGridBrush: s.setGridBrush,
      baseDepth: s.dep
    }),
    !s.varDepth ? h(FieldNumber, {
      id:"dep",
      label: "Average Depth (ft)",
      value: s.dep,
      onChange: s.setDep,
      step:"0.1",
      min:"0",
      hint: "Enter your pond's average depth, including shelves."
    }) : null
  );

case "lshape":
  return h("div",{className:"pik-row"},
    h("div", { className:"pik-dimension-tip" }, 'Tip: Type 100", 8\'4", or 8.3333 (decimal feet).'),
    h("div",{className:"pik-row pik-row--pair"},
      h(FieldFeetInches, { id:"lo", label:"Outer length", value:s.Lo, onChange:s.setLo, hideHint:true }),
      h(FieldFeetInches, { id:"wo", label:"Outer width", value:s.Wo, onChange:s.setWo, hideHint:true })
    ),
    h("div",{className:"pik-row pik-row--pair"},
      h(FieldFeetInches, { id:"li", label:"Cutout length", value:s.Li, onChange:s.setLi, hideHint:true }),
      h(FieldFeetInches, { id:"wi", label:"Cutout width", value:s.Wi, onChange:s.setWi, hideHint:true })
    ),
    h("div",{className:"pik-hint"},"Cutout must be smaller than outer rectangle."),
    h(BaseShapeCanvas, {
      shapeConfig: LShapeConfig,
      Lo: s.Lo, setLo: s.setLo,
      Wo: s.Wo, setWo: s.setWo,
      Li: s.Li, setLi: s.setLi,
      Wi: s.Wi, setWi: s.setWi,
      varDepth: s.varDepth, setVarDepth: s.setVarDepth,
      gridCellFt: s.gridCellFt,setGridCellFt: s.setGridCellFt,
      gridDepths: s.gridDepths, setGridDepths: s.setGridDepths,
      gridBrush: s.gridBrush, setGridBrush: s.setGridBrush,
      baseDepth: s.dep
    }),
    !s.varDepth ? h(FieldNumber, {
      id:"dep",
      label: "Average Depth (ft)",
      value: s.dep,
      onChange: s.setDep,
      step:"0.1",
      min:"0",
      hint: "Enter your pond's average depth, including shelves."
    }) : null
  );

case "irregular":
  return h("div", null,
    h(IrregularCanvas, {
      points: s.irrPts, setPoints: s.setIrrPts,
      closed: s.irrClosed, setClosed: s.setIrrClosed,
      drawing: s.irrDrawing, setDrawing: s.setIrrDrawing,
      ftPerPx: s.ftPerPx, setFtPerPx: s.setFtPerPx,
      refLen: s.refLen, setRefLen: s.setRefLen,
      calibMode: s.calibMode, setCalibMode: s.setCalibMode,
      calibFirst: s.calibFirst, setCalibFirst: s.setCalibFirst,
      baseDepth: s.dep,
      varDepth: s.varDepth, setVarDepth: s.setVarDepth,
      gridCellFt: s.gridCellFt,setGridCellFt: s.setGridCellFt,
      gridDepths: s.gridDepths, setGridDepths: s.setGridDepths,
      gridBrush: s.gridBrush, setGridBrush: s.setGridBrush,
      config: s.config,
    }),
    
    !s.varDepth ? h(FieldNumber, {
      id:"dep",
      label: "Average Depth (ft)",
      value: s.dep,
      onChange: s.setDep,
      step:"0.1",
      min:"0",
      hint: "Enter your pond's average depth, including shelves."
    }) : null
  );

  default: return null;
}      })()
    );
  }

// ---------- Results / Summary ----------
function ResultsCard(props) {
  var r = props.results, issues = props.issues || [];
  var _divProps = { className: "pik-card" };
if (props.refHook && typeof props.refHook === "object" && "current" in props.refHook) {
  _divProps.ref = props.refHook;
}
return h("div", _divProps,
    h("div", { className: "pik-title" }, "Results"),

    issues.length
      ? h("div", { className: "pik-help" },
          h("p", null, "Please check:"),
          h("ul", null, issues.map(function (m, i) { return h("li", { key: i }, m); }))
        )
      : null,

    // divider
    h("div", { className: "pik-divider" }),

    // metrics grid
    h("div", { className: "pik-metrics", role: "list" },
      h("div", { className: "lbl", role: "listitem" }, "Gallons"),
      h("div", { className: "val" },
        h("span", { className: "num" }, fmt(r.gal, 2)),
        h("span", { className: "unit" }, "gal")
      ),

      h("div", { className: "lbl", role: "listitem" }, "Liters"),
      h("div", { className: "val" },
        h("span", { className: "num" }, fmt(r.L, 0)),
        h("span", { className: "unit" }, "L")
      ),

      h("div", { className: "lbl", role: "listitem" }, "Surface area"),
      h("div", { className: "val" },
        h("span", { className: "num" }, fmt(r.areaFt2, 2)),
        h("span", { className: "unit" }, "ft²")
      )
    ),
        // per-depth color key
    (r.depthLegend && r.depthLegend.length)
  ? h("div", null,
      h("div", { className: "pik-divider", style:{ marginTop:8, marginBottom:8 } }),
      h("div", { className: "pik-subtle", style:{ marginBottom:6 } }, "Depth key"),

      // header row
      h("div", {
        className: "pik-subtle",
        style:{
          display:"grid",
          gridTemplateColumns:"16px 90px 120px 140px 120px",
          columnGap:8, padding:"2px 0", fontWeight:600
        }
      },
        h("span", null, ""),                 // swatch col
        h("span", null, "Depth"),
        h("span", null, "Area"),
        h("span", null, "Gallons"),
        h("span", null, "Liters")
      ),

      // rows
      h("ul", { style:{ listStyle:"none", margin:0, padding:0 } },
        r.depthLegend.map(function(entry, i){
          return h("li", {
            key:i,
            style:{
              display:"grid",
              gridTemplateColumns:"16px 90px 120px 140px 120px",
              alignItems:"center", columnGap:8, padding:"4px 0"
            }
          },
            h("span", { style:{
              width:14, height:14, borderRadius:4, display:"inline-block",
              background: entry.color, border:"1px solid rgba(0,0,0,.08)"
            }}),
            h("span", null, fmt(entry.depth, 2), " ft"),
            h("span", { className:"pik-subtle" }, fmt(entry.areaFt2, 2), " ft²"),
            h("span", { className:"pik-subtle" }, fmt(entry.gal, 2), " gal"),
            h("span", { className:"pik-subtle" }, fmt(entry.L, 0), " L")
          );
        })
      ),

      // summary row (outside the <ul>)
      h("div", { className:"pik-subtle", style:{ marginTop:6 } },
        "Sum of bands: ",
        fmt(r.depthLegend.reduce(function(a,x){ return a + (x.gal||0); }, 0), 2),
        " gal"
      )
    )
  : null
  );
}

  function SummaryCard(props) {
    var r = props.results;
    return h("div", { className:"pik-card" },
      h("div", { className:"pik-title", style:{ marginBottom:8 } }, "Summary"),
      h("div", { className:"pik-summary" },
        "Estimated pond volume ",
        h("strong", null, toFixedSafe(r.gal, 0), " gal"),
        " (", toFixedSafe(r.L, 0), " L)."
      ),
      h("div", { className:"pik-summary" },
        "Use this to size liner, pump/head loss, bio media, and UV in the Pond Planning Portal."
      )
    );
  }

  // ---------- App ----------
  function App(props) {
    var order = Array.isArray(props.order) && props.order.length ? props.order : ["controls","results","summary"];
    var cfg = props.config || {};
    var useState = React.useState, useMemo = React.useMemo, useEffect = React.useEffect;

    // Shared
    var _shape = useState("rectangle"), shape=_shape[0], setShape=_shape[1];
    var _dep = useState("4"), dep=_dep[0], setDep=_dep[1];

    // Rectangle
    var _len = useState("10"), len=_len[0], setLen=_len[1];
    var _wid = useState("8"),  wid=_wid[0], setWid=_wid[1];

    // Circle
    var _circD = useState("10"), circD=_circD[0], setCircD=_circD[1];

    // Oval
    var _ovalA = useState("12"), ovalA=_ovalA[0], setOvalA=_ovalA[1];
    var _ovalB = useState("8"),  ovalB=_ovalB[0], setOvalB=_ovalB[1];

    // L-shape
    var _Lo = useState("16"), Lo=_Lo[0], setLo=_Lo[1];
    var _Wo = useState("12"), Wo=_Wo[0], setWo=_Wo[1];
    var _Li = useState("6"),  Li=_Li[0], setLi=_Li[1];
    var _Wi = useState("4"),  Wi=_Wi[0], setWi=_Wi[1];

    // Irregular outline state
    var _irrPts = useState([]), irrPts=_irrPts[0], setIrrPts=_irrPts[1];
    var _irrClosed = useState(false), irrClosed=_irrClosed[0], setIrrClosed=_irrClosed[1];
    var _irrDrawing = useState(true), irrDrawing=_irrDrawing[0], setIrrDrawing=_irrDrawing[1];
    // Scale: feet per pixel (default 0.1 => 10 px/ft)
    var _ftPerPx = useState(0.1), ftPerPx=_ftPerPx[0], setFtPerPx=_ftPerPx[1];
    var _refLen = useState("10"), refLen=_refLen[0], setRefLen=_refLen[1];
    var _calibMode = useState(false), calibMode=_calibMode[0], setCalibMode=_calibMode[1];
    var _calibFirst = useState(null), calibFirst=_calibFirst[0], setCalibFirst=_calibFirst[1];
// Depth Grid state [PIK]
var _gridNx = useState(48), gridNx = _gridNx[0], setGridNx = _gridNx[1];
var _gridNy = useState(36), gridNy = _gridNy[0], setGridNy = _gridNy[1];
var _gridDepths = useState([]), gridDepths = _gridDepths[0], setGridDepths = _gridDepths[1];
var _gridBrush = useState("1.5"), gridBrush = _gridBrush[0], setGridBrush = _gridBrush[1];
var _varDepth = useState(false), varDepth = _varDepth[0], setVarDepth = _varDepth[1];
var _gridCellFt = useState(1), gridCellFt = _gridCellFt[0], setGridCellFt = _gridCellFt[1]; // default 1.0 ft

// keep brush synced with Average Depth field
useEffect(function(){
  if (shape!=="irregular"){ setCalibMode(false); setCalibFirst(null); }
  setIrrDrawing(shape==="irregular");
  
  // Turn off depth painting when switching shapes to prevent UI lock
  setVarDepth(false);
}, [shape]);
function autosizeGrid(areaFt2){
  var nx = 16, ny = 16;
  var rules = Array.isArray(cfg.minGridByArea) ? cfg.minGridByArea : [];
  for (var i=0; i<rules.length; i++){
    if (areaFt2 <= rules[i].maxSqFt) { nx = rules[i].nx; ny = rules[i].ny; break; }
  }
  if (nx !== gridNx || ny !== gridNy){
    setGridNx(nx); setGridNy(ny); setGridDepths([]); // re-init next render
  }
}
// ============================================================================
// BUILD PROJECT DATA FOR SAVING
// ============================================================================
function buildProjectData() {
  var projectData = {
    schema_version: "1.0",
    
    geometry: {
      shape_type: shape,
      dimensions: {},
      depth: {}
    },
    
    calculated_values: {
      volume_gallons: computed.gal || 0,
      surface_area_sqft: computed.areaFt2 || 0,
      perimeter_feet: 0
    }
  };
  
  // Add dimensions based on shape type
  if (shape === 'rectangle') {
    projectData.geometry.dimensions.length_inches = (num(len, 0) * 12);
    projectData.geometry.dimensions.width_inches = (num(wid, 0) * 12);
  } else if (shape === 'circle') {
    projectData.geometry.dimensions.diameter_inches = (num(circD, 0) * 12);
  } else if (shape === 'oval') {
    projectData.geometry.dimensions.major_axis_inches = (num(ovalA, 0) * 12);
    projectData.geometry.dimensions.minor_axis_inches = (num(ovalB, 0) * 12);
  } else if (shape === 'lshape') {
    projectData.geometry.dimensions.outer_length_inches = (num(Lo, 0) * 12);
    projectData.geometry.dimensions.outer_width_inches = (num(Wo, 0) * 12);
    projectData.geometry.dimensions.cutout_length_inches = (num(Li, 0) * 12);
    projectData.geometry.dimensions.cutout_width_inches = (num(Wi, 0) * 12);
  } else if (shape === 'irregular') {
    projectData.geometry.dimensions.outline_points = irrPts;
    projectData.geometry.dimensions.calibration = {
      ft_per_px: ftPerPx,
      reference_length_ft: num(refLen, 0)
    };
  }
  
  // Add depth data
  if (varDepth) {
    projectData.geometry.depth.type = 'custom_grid';
    projectData.geometry.depth.grid_data = {
      cell_size_ft: num(gridCellFt, 1),
      cells: gridDepths
    };
  } else {
    projectData.geometry.depth.type = 'uniform';
    projectData.geometry.depth.uniform_depth_inches = (num(dep, 0) * 12);
  }

return projectData;
}
// ============================================================================
// SAVE/LOAD STATE AND HANDLERS
// ============================================================================
var _lastSaved = useState(null);
var lastSaved = _lastSaved[0];
var setLastSaved = _lastSaved[1];

var _saveMessage = useState(null);
var saveMessage = _saveMessage[0];
var setSaveMessage = _saveMessage[1];

function handleManualSave(projectName) {
  var projectData = buildProjectData();
  
  PondDataManager.saveProject(projectData, projectName)
    .then(function(success) {
      if (success) {
        setLastSaved(new Date().toISOString());
        setSaveMessage('Project saved successfully!');
        setTimeout(function() {
          setSaveMessage(null);
        }, 3000);
      }
    })
    .catch(function(err) {
      if (err.code === 'NOT_LOGGED_IN') {
        setSaveMessage('Please log in to save your project');
      } else {
        setSaveMessage('Failed to save project. Please try again.');
      }
      setTimeout(function() {
        setSaveMessage(null);
      }, 3000);
    });
}
function handleLoadProject(projectData) {
  if (!projectData || !projectData.geometry) {
    setSaveMessage('Invalid project data');
    setTimeout(function() { setSaveMessage(null); }, 3000);
    return;
  }
  
  // Set shape first
  setShape(projectData.geometry.shape_type || 'rectangle');
  
  // Load dimensions based on shape
  var dims = projectData.geometry.dimensions || {};
  
  if (projectData.geometry.shape_type === 'rectangle') {
    setLen(String((dims.length_inches || 0) / 12));
    setWid(String((dims.width_inches || 0) / 12));
  } else if (projectData.geometry.shape_type === 'circle') {
    setCircD(String((dims.diameter_inches || 0) / 12));
  } else if (projectData.geometry.shape_type === 'oval') {
    setOvalA(String((dims.major_axis_inches || 0) / 12));
    setOvalB(String((dims.minor_axis_inches || 0) / 12));
  } else if (projectData.geometry.shape_type === 'lshape') {
    setLo(String((dims.outer_length_inches || 0) / 12));
    setWo(String((dims.outer_width_inches || 0) / 12));
    setLi(String((dims.cutout_length_inches || 0) / 12));
    setWi(String((dims.cutout_width_inches || 0) / 12));
  } else if (projectData.geometry.shape_type === 'irregular') {
    if (Array.isArray(dims.outline_points)) {
      setIrrPts(dims.outline_points);
      setIrrClosed(dims.outline_points.length >= 3);
    }
    if (dims.calibration) {
      setFtPerPx(dims.calibration.ft_per_px || 0.1);
      setRefLen(String(dims.calibration.reference_length_ft || 10));
    }
  }
  
  // Load depth data
  var depth = projectData.geometry.depth || {};
  
  if (depth.type === 'custom_grid' && depth.grid_data) {
    setVarDepth(true);
    setGridCellFt(depth.grid_data.cell_size_ft || 1);
    if (Array.isArray(depth.grid_data.cells)) {
      setGridDepths(depth.grid_data.cells);
    }
  } else if (depth.type === 'uniform') {
    setVarDepth(false);
    setDep(String((depth.uniform_depth_inches || 0) / 12));
  }
  
  setSaveMessage('Project loaded successfully!');
  setTimeout(function() { setSaveMessage(null); }, 3000);
  
  // Mark this as the active project (only if it has a project id)
  if (projectData.project && projectData.project.id) {
    PondDataManager.setActiveProject(projectData.project.id);
  }
}

// Handle saving draft before login
function handleSaveDraft() {
  var draftData = buildProjectData();
  PondDataManager.saveDraft(draftData);
}

// Restore draft on mount if user just logged in
useEffect(function() {
  var draft = PondDataManager.getDraft();
  if (draft && PondDataManager.isLoggedIn()) {
    // User has returned after logging in - restore their work
    handleLoadProject(draft);
    // Clear the draft after successful restore
    PondDataManager.clearDraft();
    setSaveMessage('Your work has been restored!');
    setTimeout(function() { setSaveMessage(null); }, 3000);
  }
}, []); // Run once on mount

    // Compute area + volume
    var computed = useMemo(function(){
      var d = num(dep, 0);
      var issues = [];
      // helper: sum painted cu ft over any polygon (in SVG px)
function sumPaintedCuFtFor(polyPts, ftPerPxLocal){
  if (!Array.isArray(polyPts) || polyPts.length < 3) return null;
  if (!(ftPerPxLocal > 0)) return null;
  // derive grid dims for 560×360 window using the SAME cell size/current scale
  var VBWg = 560, VBHg = 360;
  var dimsG = gridDimsFromCell(ftPerPxLocal, gridCellFt, VBWg, VBHg);
  var nxG = dimsG.nx, nyG = dimsG.ny, cellPxG = dimsG.cellPx;

  // painted array must match the current grid dims, otherwise wait a frame
  if (!Array.isArray(gridDepths) || gridDepths.length !== nxG * nyG) return null;

  var cellAreaFt2 = Math.max(0, parseFloat(gridCellFt) || 1) ** 2; // square ft²
  var S = 8; // 8×8 samples per cell for fractional coverage

  var total = 0;
  for (var iy=0; iy<nyG; iy++){
    for (var ix=0; ix<nxG; ix++){
      var frac = cellCoverageFractionSquare(ix, iy, cellPxG, VBWg, VBHg, polyPts, S);
      if (frac <= 0) continue;
      var dcell = Math.max(0, num(gridDepths[iy*nxG + ix], 0));
      total += dcell * cellAreaFt2 * frac; // ft depth × ft² area × coverage
    }
  }
  return total; // cubic feet
}

      if (d <= 0) issues.push("Average depth must be greater than 0.");

      var areaFt2 = 0;
var cuftGrid = null; // grid-only override

      switch (shape){
        case "rectangle": {
          var L=num(len,0), W=num(wid,0);
          if (L<=0 || W<=0) issues.push("Length and width must be greater than 0.");
          areaFt2 = areaRectangle(L, W);
          // when painting is ON, compute volume from grid (same geometry as ShapeCanvasRect)
if (varDepth && L > 0 && W > 0){
  var VBWr = 560, VBHr = 360;
  var PADr = 8, GAPr = 6, LABEL_WR = 140, LABEL_HR = 28, LINE_OUT_XR = 24, LINE_OUT_YR = 16;
  var MARGr = {
    left: 24,
    top: 24,
    right: PADr + LINE_OUT_XR + GAPr + LABEL_WR,
    bottom: PADr + LINE_OUT_YR + GAPr + LABEL_HR
  };
  var usableWr = Math.max(40, VBWr - (MARGr.left + MARGr.right));
  var usableHr = Math.max(40, VBHr - (MARGr.top + MARGr.bottom));
  var pxPerFtr = 10;
  if (L > 0 || W > 0) {
    var sXr = (W > 0) ? (usableWr / Math.max(W, 0.0001)) : Infinity;
    var sYr = (L > 0) ? (usableHr / Math.max(L, 0.0001)) : Infinity;
    pxPerFtr = Math.min(sXr, sYr);
    pxPerFtr = Math.max(4, Math.min(40, pxPerFtr));
  }
  var ftPerPxr = 1 / pxPerFtr;
  var pxWr = Math.max(0, W * pxPerFtr);
  var pxLr = Math.max(0, L * pxPerFtr);
  var neededWr = MARGr.left + pxWr + MARGr.right;
  var neededHr = MARGr.top  + pxLr + MARGr.bottom;
  var offsetXr = Math.max(0, (VBWr - neededWr) / 2);
  var offsetYr = Math.max(0, (VBHr - neededHr) / 2);
  var x0r = MARGr.left + offsetXr,  x1r = x0r + pxWr;
  var y0r = MARGr.top  + offsetYr,  y1r = y0r + pxLr;

  var polyRect = makeRectPtsPx(x0r, y0r, x1r, y1r);
  cuftGrid = sumPaintedCuFtFor(polyRect, ftPerPxr);
}

          break;
        }
        case "circle": {
          var D=num(circD,0);
          if (D<=0) issues.push("Circle diameter must be greater than 0.");
          areaFt2 = areaCircle(D);
          // when painting is ON, compute volume from grid (same geometry as ShapeCanvasCircle)
if (varDepth && D > 0){
  var VBWc = 560, VBHc = 360;
  var PADc = 8, GAPc = 6, LABEL_WC = 140, LABEL_HC = 28, LINE_OUT_XC = 24, LINE_OUT_YC = 16;
  var MARGc = { left:24, top:24, right: PADc + LINE_OUT_XC + GAPc + LABEL_WC, bottom: PADc + LINE_OUT_YC + GAPc + LABEL_HC };
  var usableWc = Math.max(40, VBWc - (MARGc.left + MARGc.right));
  var usableHc = Math.max(40, VBHc - (MARGc.top + MARGc.bottom));
  var pxPerFtc = Math.max(4, Math.min(40, (D > 0) ? Math.min(usableWc, usableHc) / D : 10));
  var ftPerPxc = 1 / pxPerFtc;

  var pxDc = Math.max(0, D * pxPerFtc);
  var neededWc = MARGc.left + pxDc + MARGc.right;
  var neededHc = MARGc.top  + pxDc + MARGc.bottom;
  var offsetXc = Math.max(0, (VBWc - neededWc)/2);
  var offsetYc = Math.max(0, (VBHc - neededHc)/2);
  var x0c = MARGc.left + offsetXc, x1c = x0c + pxDc;
  var y0c = MARGc.top  + offsetYc, y1c = y0c + pxDc;
  var cxc = (x0c + x1c)/2, cyc = (y0c + y1c)/2, rc = pxDc/2;

  var clipPtsC = makeEllipsePtsPx(cxc, cyc, rc, rc, 72);
  cuftGrid = sumPaintedCuFtFor(clipPtsC, ftPerPxc);
}

          break;
        }
        case "oval": {
          var A=num(ovalA,0), B=num(ovalB,0);
          if (A<=0 || B<=0) issues.push("Oval axes must be greater than 0.");
          areaFt2 = areaEllipse(A, B);
          // when painting is ON, compute volume from grid (same geometry as ShapeCanvasOval)
if (varDepth && A > 0 && B > 0){
  var VBWo = 560, VBHo = 360;
  var PADo = 8, GAPo = 6, LABEL_WO = 140, LABEL_HO = 28, LINE_OUT_XO = 24, LINE_OUT_YO = 16;
  var Wfto = A, Lfto = B;
  var MARGo = { left:24, top:24, right: PADo + LINE_OUT_XO + GAPo + LABEL_WO, bottom: PADo + LINE_OUT_YO + GAPo + LABEL_HO };
  var usableWo = Math.max(40, VBWo - (MARGo.left + MARGo.right));
  var usableHo = Math.max(40, VBHo - (MARGo.top + MARGo.bottom));
  var pxPerFto = (Wfto>0 && Lfto>0) ? Math.max(4, Math.min(40, Math.min(usableWo/Wfto, usableHo/Lfto))) : 10;
  var ftPerPxo = 1 / pxPerFto;

  var pxWo = Math.max(0, Wfto * pxPerFto);
  var pxLo = Math.max(0, Lfto * pxPerFto);
  var neededWo = MARGo.left + pxWo + MARGo.right;
  var neededHo = MARGo.top  + pxLo + MARGo.bottom;
  var offsetXo = Math.max(0, (VBWo - neededWo)/2);
  var offsetYo = Math.max(0, (VBHo - neededHo)/2);
  var x0o = MARGo.left + offsetXo, x1o = x0o + pxWo;
  var y0o = MARGo.top  + offsetYo, y1o = y0o + pxLo;

  var cxo = (x0o + x1o)/2, cyo = (y0o + y1o)/2;
  var rxo = pxWo/2, ryo = pxLo/2;

  var clipPtsO = makeEllipsePtsPx(cxo, cyo, rxo, ryo, 72);
  cuftGrid = sumPaintedCuFtFor(clipPtsO, ftPerPxo);
}

          break;
        }

        case "lshape": {
  var LO=num(Lo,0), WO=num(Wo,0), LI=num(Li,0), WI=num(Wi,0);
  if (LO<=0 || WO<=0) issues.push("Outer dimensions must be greater than 0.");
  if (LI<0 || WI<0) issues.push("Cutout dimensions cannot be negative.");
  if (LI>=LO || WI>=WO) issues.push("Cutout must be smaller than the outer rectangle.");
  areaFt2 = areaLShape(LO, WO, clamp(LI,0,LO), clamp(WI,0,WO));
  
  // when painting is ON, compute volume from grid
  if (varDepth && LO > 0 && WO > 0 && LI < LO && WI < WO) {
    var VBWl = 560, VBHl = 360;
    var PADl = 8, GAPl = 6, LABEL_Wl = 140, LABEL_Hl = 28, LINE_OUT_Xl = 24, LINE_OUT_Yl = 16;
    var MARGl = {
      left: 24,
      top: 24,
      right: PADl + LINE_OUT_Xl + GAPl + LABEL_Wl,
      bottom: PADl + LINE_OUT_Yl + GAPl + LABEL_Hl
    };
    var usableWl = Math.max(40, VBWl - (MARGl.left + MARGl.right));
    var usableHl = Math.max(40, VBHl - (MARGl.top + MARGl.bottom));
    var pxPerFtl = 10;
    if (LO > 0 && WO > 0) {
      var sXl = usableWl / Math.max(LO, 0.0001);
      var sYl = usableHl / Math.max(WO, 0.0001);
      pxPerFtl = Math.min(sXl, sYl);
      pxPerFtl = Math.max(4, Math.min(40, pxPerFtl));
    }
    var ftPerPxl = 1 / pxPerFtl;
    
    var pxLol = Math.max(0, LO * pxPerFtl);
    var pxWol = Math.max(0, WO * pxPerFtl);
    var pxLil = Math.max(0, LI * pxPerFtl);
    var pxWil = Math.max(0, WI * pxPerFtl);
    
    var neededWl = MARGl.left + pxLol + MARGl.right;
    var neededHl = MARGl.top + pxWol + MARGl.bottom;
    var offsetXl = Math.max(0, (VBWl - neededWl) / 2);
    var offsetYl = Math.max(0, (VBHl - neededHl) / 2);
    
    var x0l = MARGl.left + offsetXl, x1l = x0l + pxLol;
    var y0l = MARGl.top + offsetYl, y1l = y0l + pxWol;
    
    var cutX0l = x1l - pxLil;
    var cutY1l = y0l + pxWil;
    
    var polyLShape = [
      {x:x0l, y:y0l}, {x:cutX0l, y:y0l}, {x:cutX0l, y:cutY1l},
      {x:x1l, y:cutY1l}, {x:x1l, y:y1l}, {x:x0l, y:y1l}
    ];
    
    cuftGrid = sumPaintedCuFtFor(polyLShape, ftPerPxl);
  }
  break;
}

case "irregular": {
  if (irrPts.length < 3) issues.push("Add at least 3 points for the outline.");
  if (!irrClosed) issues.push("Close the shape to calculate area.");
  
  // Check if still using default uncalibrated scale
  if (ftPerPx === 0.1) {
    issues.push("Set scale using the Calibrate button (click 2 points with known distance).");
    areaFt2 = 0; // Don't calculate with uncalibrated scale
    break;
  }
  
  if (ftPerPx <= 0) issues.push("Set scale using the calibration tool (ft per px).");
  var apx = (irrClosed && irrPts.length>=3) ? polygonAreaPx(irrPts) : 0;
  areaFt2 = apx * ftPerPx * ftPerPx;
  break;
}
        default: areaFt2 = 0;
      }
if (shape === "irregular" && varDepth && irrClosed && ftPerPx > 0) {
  var VBW2 = 560, VBH2 = 360;
  var dims2 = gridDimsFromCell(ftPerPx, gridCellFt, VBW2, VBH2);
  var nx2 = dims2.nx, ny2 = dims2.ny, cellPx2 = dims2.cellPx;
  var cellAreaFt2_2 = Math.max(0, parseFloat(gridCellFt)||1) ** 2;
  var S2 = 4;
  if (Array.isArray(gridDepths) && gridDepths.length === nx2*ny2) {
    var total2 = 0;
    for (var iy2=0; iy2<ny2; iy2++){
      for (var ix2=0; ix2<nx2; ix2++){
        var frac2 = cellCoverageFractionSquare(ix2, iy2, cellPx2, VBW2, VBH2, irrPts, S2);
        if (frac2 <= 0) continue;
        var dcell2 = Math.max(0, num(gridDepths[iy2*nx2 + ix2], 0));
        total2 += dcell2 * cellAreaFt2_2 * frac2;
      }
    }
    cuftGrid = total2;
  }
}

      var cuft =
  (varDepth && (shape === "irregular" || shape === "rectangle" || shape === "circle" || shape === "oval" || shape === "lshape") && cuftGrid != null)
    ? cuftGrid
    : (areaFt2 * Math.max(0, d));

      var gal = cuft * 7.48052;
      var cum = cuft * 0.028316846592;
      var liters = cum * 1000;

      var r = Number.isFinite(cfg.rounding) ? Math.max(0, Math.round(cfg.rounding)) : 0;
      if (r > 0){
        var pow = Math.pow(10, r);
        gal = Math.round(gal*pow)/pow;
        liters = Math.round(liters*pow)/pow;
        areaFt2 = Math.round(areaFt2*pow)/pow;
      }
// Build per-depth legend (area per distinct painted depth inside the ACTIVE shape polygon)
var depthLegend = [];
if (varDepth &&
    (shape === "irregular" || shape === "rectangle" || shape === "circle" || shape === "oval" || shape === "lshape") &&
    Array.isArray(gridDepths) && gridDepths.length > 0) {

  var VBW3 = 560, VBH3 = 360;

  // Geometry helpers that mirror each canvas' layout math so our polygon + ftPerPx match painting
  function rectGeom(L, W) {
    var PAD=8, GAP=6, LABEL_W=140, LABEL_H=28, LINE_OUT_X=24, LINE_OUT_Y=16;
    var MARG = { left:24, top:24, right: PAD+LINE_OUT_X+GAP+LABEL_W, bottom: PAD+LINE_OUT_Y+GAP+LABEL_H };
    var usableW = Math.max(40, VBW3 - (MARG.left + MARG.right));
    var usableH = Math.max(40, VBH3 - (MARG.top  + MARG.bottom));
    var pxPerFt = 10;
    if (L > 0 || W > 0) {
      var sX = (W > 0) ? (usableW / Math.max(W, 0.0001)) : Infinity;
      var sY = (L > 0) ? (usableH / Math.max(L, 0.0001)) : Infinity;
      pxPerFt = Math.min(sX, sY);
      pxPerFt = Math.max(4, Math.min(40, pxPerFt));
    }
    var ftPerPxLocal = 1 / pxPerFt;
    var pxW = Math.max(0, W * pxPerFt);
    var pxL = Math.max(0, L * pxPerFt);
    var neededW = MARG.left + pxW + MARG.right;
    var neededH = MARG.top  + pxL + MARG.bottom;
    var offsetX = Math.max(0, (VBW3 - neededW) / 2);
    var offsetY = Math.max(0, (VBH3 - neededH) / 2);
    var x0 = MARG.left + offsetX,  x1 = x0 + pxW;
    var y0 = MARG.top  + offsetY,  y1 = y0 + pxL;
    return { ok: (L > 0 && W > 0), ftPerPx: ftPerPxLocal, poly: makeRectPtsPx(x0, y0, x1, y1) };
  }

  function circleGeom(D) {
    var PAD=8, GAP=6, LABEL_W=140, LABEL_H=28, LINE_OUT_X=24, LINE_OUT_Y=16;
    var MARG = { left:24, top:24, right: PAD+LINE_OUT_X+GAP+LABEL_W, bottom: PAD+LINE_OUT_Y+GAP+LABEL_H };
    var usableW = Math.max(40, VBW3 - (MARG.left + MARG.right));
    var usableH = Math.max(40, VBH3 - (MARG.top  + MARG.bottom));
    var pxPerFt = (D > 0) ? Math.max(4, Math.min(40, Math.min(usableW, usableH) / D)) : 10;
    var ftPerPxLocal = 1 / pxPerFt;
    var pxD = Math.max(0, D * pxPerFt);
    var neededW = MARG.left + pxD + MARG.right;
    var neededH = MARG.top  + pxD + MARG.bottom;
    var offsetX = Math.max(0, (VBW3 - neededW) / 2);
    var offsetY = Math.max(0, (VBH3 - neededH) / 2);
    var x0 = MARG.left + offsetX, x1 = x0 + pxD;
    var y0 = MARG.top  + offsetY, y1 = y0 + pxD;
    var cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, r = pxD / 2;
    return { ok: (D > 0), ftPerPx: ftPerPxLocal, poly: makeEllipsePtsPx(cx, cy, r, r, 72) };
  }

  function ovalGeom(A, B) {
    var PAD=8, GAP=6, LABEL_W=140, LABEL_H=28, LINE_OUT_X=24, LINE_OUT_Y=16;
    var MARG = { left:24, top:24, right: PAD+LINE_OUT_X+GAP+LABEL_W, bottom: PAD+LINE_OUT_Y+GAP+LABEL_H };
    var usableW = Math.max(40, VBW3 - (MARG.left + MARG.right));
    var usableH = Math.max(40, VBH3 - (MARG.top  + MARG.bottom));
    var pxPerFt = (A > 0 && B > 0) ? Math.max(4, Math.min(40, Math.min(usableW / A, usableH / B))) : 10;
    var ftPerPxLocal = 1 / pxPerFt;
    var pxW = Math.max(0, A * pxPerFt);
    var pxL = Math.max(0, B * pxPerFt);
    var neededW = MARG.left + pxW + MARG.right;
    var neededH = MARG.top  + pxL + MARG.bottom;
    var offsetX = Math.max(0, (VBW3 - neededW) / 2);
    var offsetY = Math.max(0, (VBH3 - neededH) / 2);
    var x0 = MARG.left + offsetX, x1 = x0 + pxW;
    var y0 = MARG.top  + offsetY, y1 = y0 + pxL;
    var cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    var rx = pxW / 2, ry = pxL / 2;
    return { ok: (A > 0 && B > 0), ftPerPx: ftPerPxLocal, poly: makeEllipsePtsPx(cx, cy, rx, ry, 72) };
  }

// Pick legend geometry for the active shape
var legendGeom = null;
if (shape === "rectangle") {
  legendGeom = rectGeom(num(len, 0), num(wid, 0));
} else if (shape === "circle") {
  legendGeom = circleGeom(num(circD, 0));
} else if (shape === "oval") {
  legendGeom = ovalGeom(num(ovalA, 0), num(ovalB, 0));
} else if (shape === "lshape") {
  var LOleg = num(Lo, 0), WOleg = num(Wo, 0), LIleg = num(Li, 0), WIleg = num(Wi, 0);
  if (LOleg > 0 && WOleg > 0 && LIleg < LOleg && WIleg < WOleg) {
    var PADleg = 8, GAPleg = 6, LABEL_Wleg = 140, LABEL_Hleg = 28, LINE_OUT_Xleg = 24, LINE_OUT_Yleg = 16;
    var MARGleg = { left:24, top:24, right: PADleg + LINE_OUT_Xleg + GAPleg + LABEL_Wleg, bottom: PADleg + LINE_OUT_Yleg + GAPleg + LABEL_Hleg };
    var usableWleg = Math.max(40, VBW3 - (MARGleg.left + MARGleg.right));
    var usableHleg = Math.max(40, VBH3 - (MARGleg.top + MARGleg.bottom));
    var pxPerFtleg = 10;
    if (LOleg > 0 && WOleg > 0) {
      var sXleg = usableWleg / Math.max(LOleg, 0.0001);
      var sYleg = usableHleg / Math.max(WOleg, 0.0001);
      pxPerFtleg = Math.min(sXleg, sYleg);
      pxPerFtleg = Math.max(4, Math.min(40, pxPerFtleg));
    }
    var ftPerPxleg = 1 / pxPerFtleg;
    var pxLoleg = Math.max(0, LOleg * pxPerFtleg);
    var pxWoleg = Math.max(0, WOleg * pxPerFtleg);
    var pxLileg = Math.max(0, LIleg * pxPerFtleg);
    var pxWileg = Math.max(0, WIleg * pxPerFtleg);
    var neededWleg = MARGleg.left + pxLoleg + MARGleg.right;
    var neededHleg = MARGleg.top + pxWoleg + MARGleg.bottom;
    var offsetXleg = Math.max(0, (VBW3 - neededWleg) / 2);
    var offsetYleg = Math.max(0, (VBH3 - neededHleg) / 2);
    var x0leg = MARGleg.left + offsetXleg, x1leg = x0leg + pxLoleg;
    var y0leg = MARGleg.top + offsetYleg, y1leg = y0leg + pxWoleg;
    var cutX0leg = x1leg - pxLileg;
    var cutY1leg = y0leg + pxWileg;
    var polyLeg = [
      {x:x0leg, y:y0leg}, {x:cutX0leg, y:y0leg}, {x:cutX0leg, y:cutY1leg},
      {x:x1leg, y:cutY1leg}, {x:x1leg, y:y1leg}, {x:x0leg, y:y1leg}
    ];
    legendGeom = { ok:true, ftPerPx: ftPerPxleg, poly: polyLeg };
  }
} else if (shape === "irregular") {
  legendGeom = (irrClosed && Array.isArray(irrPts) && irrPts.length >= 3 && ftPerPx > 0)
    ? { ok:true, ftPerPx: ftPerPx, poly: irrPts }
    : null;
}

  if (legendGeom && legendGeom.ok && legendGeom.ftPerPx > 0 &&
      Array.isArray(legendGeom.poly) && legendGeom.poly.length >= 3) {

    var dims3 = gridDimsFromCell(legendGeom.ftPerPx, gridCellFt, VBW3, VBH3);
    var nx3 = dims3.nx, ny3 = dims3.ny, cellPx3 = dims3.cellPx;

    // Only build legend if our gridDepths matches the current grid dimensions
    if (gridDepths.length === nx3 * ny3) {
      var cellAreaFt2_3 = Math.max(0, parseFloat(gridCellFt) || 1) ** 2;
      var S3 = 4, maxDepthUsed = 0;
      var agg = Object.create(null);

      for (var jy = 0; jy < ny3; jy++) {
        for (var jx = 0; jx < nx3; jx++) {
          var frac3 = cellCoverageFractionSquare(jx, jy, cellPx3, VBW3, VBH3, legendGeom.poly, S3);
          if (frac3 <= 0) continue;
          var dval = Math.max(0, num(gridDepths[jy * nx3 + jx], 0));
          if (dval > maxDepthUsed) maxDepthUsed = dval;
          var key = dval.toFixed(2); // band by 0.01 ft
          agg[key] = (agg[key] || 0) + cellAreaFt2_3 * frac3;
        }
      }

      depthLegend = Object.keys(agg)
        .sort(function(a, b) { return parseFloat(a) - parseFloat(b); })
        .map(function(k) {
          var dft = parseFloat(k);
          var area = agg[k];
          var cuftBand = area * dft;
          var galBand  = cuftBand * 7.48052;
          var LBand    = cuftBand * 28.316846592;
          return {
            depth: dft,
            areaFt2: area,
            cuft: cuftBand,
            gal: galBand,
            L: LBand,
            color: depthColor(dft, maxDepthUsed)
          };
        });
    }
  }
}

      return {
        areaFt2: areaFt2,
        dep: Math.max(0, d),
        gal: gal, L: liters,
        issues: issues,
        depthLegend: depthLegend
      };
    }, [
      shape, dep,
      len, wid,
      circD,
      ovalA, ovalB,
      Lo, Wo, Li, Wi,
      gridNx, gridNy, gridDepths,
      irrPts, irrClosed, ftPerPx, cfg.rounding,
      varDepth,
      gridCellFt
    ]);

    // Results action handlers + ref
    var resultsRef = React.useRef(null);
var state = {
  // shared
  shape: shape, setShape: setShape,
  dep: dep, setDep: setDep,
  // rectangle
  len: len, setLen: setLen, wid: wid, setWid: setWid,
  // circle
  circD: circD, setCircD: setCircD,
  // oval
  ovalA: ovalA, setOvalA: setOvalA, ovalB: ovalB, setOvalB: setOvalB,
  // l-shape
  Lo: Lo, setLo: setLo, Wo: Wo, setWo: setWo, Li: Li, setLi: setLi, Wi: Wi, setWi: setWi,
  // depth grid
  gridNx: gridNx, setGridNx: setGridNx,
  gridNy: gridNy, setGridNy: setGridNy,
  gridDepths: gridDepths, setGridDepths: setGridDepths,
  gridBrush: gridBrush, setGridBrush: setGridBrush,
  autosizeGrid: autosizeGrid,
  varDepth: varDepth, setVarDepth: setVarDepth,
  gridCellFt: gridCellFt, setGridCellFt: setGridCellFt,
  // irregular
  irrPts: irrPts, setIrrPts: setIrrPts,
  irrClosed: irrClosed, setIrrClosed: setIrrClosed,
  irrDrawing: irrDrawing, setIrrDrawing: setIrrDrawing,
  ftPerPx: ftPerPx, setFtPerPx: setFtPerPx,
  refLen: refLen, setRefLen: setRefLen,
  calibMode: calibMode, setCalibMode: setCalibMode,
  calibFirst: calibFirst, setCalibFirst: setCalibFirst,
  config: props.config || {},
  handleManualSave: handleManualSave,
  handleLoadProject: handleLoadProject,
  handleSaveDraft: handleSaveDraft,
  activeProject: PondDataManager.getActiveProject(),
  lastSaved: lastSaved,
  onSaveSuccess: function(message) {
    setSaveMessage(message);
    setTimeout(function() { setSaveMessage(null); }, 3000);
  }
};

    var cards = {
  controls: h(ErrorBoundary, { key:"controls-eb" },
               h(ControlsCard, { key:"controls", state: state })),

  results:  h(ErrorBoundary, { key:"results-eb" },
               h(ResultsCard,  {
                 key:"results",
                 results: computed,
                 issues: computed.issues,
                 refHook: resultsRef
               })),

  summary:  h(ErrorBoundary, { key:"summary-eb" },
               h(SummaryCard,  { key:"summary", results: computed }))
};


    return h("div", { className: "pik-volume-calc" },
  // Save notification (fixed position toast)
  saveMessage && h("div", { className: "pond-save-toast" },
    saveMessage
  ),
  
  // Existing calculator cards
  order.map(function(k){ return cards[k] || null; })
);
  }

  // ---------- Public API ----------
  function mount(el, opts) {
     console.log('[PIK] mount() called with opts:', opts);
    if (!el) return;
    if (!(window.React && window.ReactDOM && window.ReactDOM.createRoot)) {
      console.warn("[PIK_VOLUME] React 18 UMD not available yet; retrying…");
      setTimeout(function(){ mount(el, opts); }, 50);
      return;
    }
    try {
      var root = ReactDOM.createRoot(el);
      var props = Object.assign({ calcId:"volume", mode:"standard", order:["controls","results","summary"], config:{} }, opts||{});
      ROOTS.set(el, { root: root, props: props });
      root.render(h(App, props));
    } catch (e) { console.error("[PIK_VOLUME] mount failed:", e); }
  }
  function unmount(el) {
    var rec = ROOTS.get(el);
    if (rec && rec.root) { try { rec.root.unmount(); } catch (e) {} ROOTS.delete(el); }
  }

  window.PIK_VOLUME = window.PIK_VOLUME || {};
  window.PIK_VOLUME.mount = mount;
  window.PIK_VOLUME.unmount = unmount;
})();
